import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Link, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import {
  FaArrowRight,
  FaChartLine,
  FaCheckCircle,
  FaDice,
  FaExclamationTriangle,
  FaLock,
  FaPencilAlt,
  FaPlay,
  FaRobot,
  FaShieldAlt,
  FaStop,
  FaTrash,
  FaWallet,
} from 'react-icons/fa';
import NavigationBar from './components/NavigationBar';
import LoadingSpinner from './components/LoadingSpinner';
import StockFocusCompanyDrawer from './components/StockFocusCompanyDrawer';
import { BotIdentity } from './components/BotIdentity';
import { BotLiveActivity, useBotLiveActivity } from './components/BotLiveActivity';
import CustomStrategyEditor from './components/CustomStrategyEditor';
import PercentageStrategySetup from './PercentageStrategySetup';
import ProtectiveStrategySetup from './ProtectiveStrategySetup';
import { protectiveSettingsFromDefinition } from './protectiveStrategyPreset';
import {
  PERCENTAGE_PRESET, percentageComparisonDetails, percentageSettingsFromDefinition,
} from './percentageStrategyPreset';
import { AuthContext, axiosInstance } from './AuthContext';
import {
  clearSimpleBotSetup,
  EMPTY_SIMPLE_BOT_SETUP,
  nextDefaultBotName,
  parseSymbolInput,
  readSimpleBotSetup,
  simpleBotSetupOwnerFromToken,
  writeSimpleBotSetup,
} from './simpleBotSetup';
import {
  DAILY_NEXT_OPEN_DISCLOSURE,
  FACTORY_STRATEGY_EDUCATION,
  PRACTICE_EXPLANATION,
  pairingExplanation,
  strategyDisplayName,
} from './beginnerEducation';
import { presentBotReadiness } from './botReadiness';
import { canPrepareBot } from './botLifecycleCapabilities';
import {
  isEditableCustomEvaluator,
  maximumHoldingReview,
  maximumHoldingState,
  validateMaximumHolding,
} from './customStrategyTimeExit';
import { formatPercent } from './advancedTradingSettings';
import usePremiumEntitlement from './hooks/usePremiumEntitlement';
import { isPremiumPlan } from './backtests';
import { backtestHandoffPath } from './backtestHandoff';
import {
  factoryStrategyThresholds,
  pickAnotherLabel,
  stockFocusSelectionLineage,
} from './botCreationPresentation';
import { startBotCreatorScrollHandoff } from './botCreatorScrollHandoff';
import {
  customerBotCreationError,
  isBotStructuralEligibilityError,
} from './botCreationFeedback';

const BOT_ACCENTS = [
  ['from-blue-500 to-cyan-400', 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200'],
  ['from-violet-500 to-fuchsia-400', 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-200'],
  ['from-emerald-500 to-teal-400', 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200'],
  ['from-amber-500 to-orange-400', 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200'],
];

const InlineBotActionFeedback = ({ feedback, id }) => {
  if (!feedback) return null;
  const tone = feedback.tone;
  return (
    <p
      id={id}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      className={`rounded-xl p-3 text-sm font-semibold ${tone === 'error'
        ? 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'
        : tone === 'success'
          ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200'
          : 'bg-cyan-50 text-cyan-900 dark:bg-cyan-950/40 dark:text-cyan-200'}`}
    >
      {feedback.message}
    </p>
  );
};

const PRESET_GUIDANCE = {
  'Blue-Chip Stability': {
    looksFor: 'Established, large companies with steadier prices and dividend support.',
    difference: 'Favors stronger cash flow, moderate debt, and more restrained valuations.',
    risk: 'Lower relative stock-selection risk; company prices can still fall.',
    suitableFor: 'People who prefer established businesses over rapid-growth candidates.',
    notGuarantee: 'It does not guarantee stability, dividends, or protection from a market decline.',
  },
  'Growth Momentum': {
    looksFor: 'Companies reporting stronger revenue and earnings growth.',
    difference: 'Allows higher valuations and more price movement in exchange for growth potential.',
    risk: 'Higher-risk stock focus with larger possible swings.',
    suitableFor: 'People comfortable watching growth-oriented companies.',
    notGuarantee: 'It does not guarantee that growth continues or that a higher valuation is justified.',
  },
  'Value Investing': {
    looksFor: 'Companies with lower valuation ratios and established fundamentals.',
    difference: 'Applies tighter valuation and debt limits than the growth profiles.',
    risk: 'Moderate stock-selection risk; a low valuation can reflect real business problems.',
    suitableFor: 'People who want valuation-conscious candidates.',
    notGuarantee: 'It does not prove a stock is undervalued; a low valuation can reflect genuine problems.',
  },
  'Profitable Growth': {
    looksFor: 'Growing non-financial companies that already show positive operating profitability and cash generation.',
    difference: 'Combines reported revenue and earnings growth with positive operating margin and free cash flow.',
    risk: 'Higher-risk stock focus; recent growth and profitability may not continue.',
    suitableFor: 'People seeking growing businesses that have moved beyond loss-only growth.',
    notGuarantee: 'It does not guarantee that current growth, profitability, or cash generation will continue.',
  },
  'High Volatility': {
    looksFor: 'Stocks with greater price sensitivity and room for larger swings.',
    difference: 'Uses the broadest allowances for beta, debt, company size, and valuation.',
    risk: 'Highest-risk Pinngro Stock Focus; losses can move quickly.',
    suitableFor: 'People who understand and accept substantial price movement.',
    notGuarantee: 'Higher volatility does not guarantee higher returns or predictable trading opportunities.',
  },
  'Dividend Quality': {
    looksFor: 'Current dividend-paying companies with established size, profitability, and liquidity.',
    difference: 'Requires verified current dividend evidence and bounded earnings valuation.',
    risk: 'Lower-to-moderate stock focus; dividends can be reduced or stopped.',
    suitableFor: 'People interested in current income-oriented candidates.',
    notGuarantee: 'It does not verify historical dividend growth or guarantee future payments.',
  },
  'Technology Leaders': {
    looksFor: 'Large, liquid companies authoritatively classified in Information Technology industries.',
    difference: 'Uses provider Sector/GICS identity rather than company-name or description matching.',
    risk: 'Higher-risk stock focus; growth expectations can change quickly.',
    suitableFor: 'People researching established software, hardware, and semiconductor businesses.',
    notGuarantee: 'It does not certify AI involvement or guarantee technology leadership.',
  },
  'Defensive Stocks': {
    looksFor: 'Established Consumer Staples, Health Care, and Utilities companies with restrained price movement.',
    difference: 'Requires authoritative defensive-sector identity plus lower beta and ATR.',
    risk: 'Lower relative stock-selection risk, not protection from market losses.',
    suitableFor: 'People who prefer more conservative candidates.',
    notGuarantee: 'Defensive sector membership and lower volatility do not protect against losses.',
  },
};

const humanizeMetric = (key) => key
  .replace(/^(min|max)_/, '')
  .replaceAll('_', ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const summarizeCriteria = (template) => {
  if (Array.isArray(template?.rules)) {
    return template.rules.slice(0, 3).map((rule) => ({
      label: humanizeMetric(rule.field || 'Rule'),
      value: rule.explanation || `${rule.operator} ${String(rule.value)}`,
    }));
  }
  return Object.entries(template || {})
    .filter(([key, value]) => key !== 'description' && value !== null && value !== false)
    .slice(0, 3)
    .map(([key, value]) => ({
      label: humanizeMetric(key),
      value: typeof value === 'boolean' ? 'Yes' : value.toLocaleString(),
    }));
};

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  style: 'currency', currency: 'USD', maximumFractionDigits: 2,
});

const activePendingAction = (paperState) => paperState?.pending_actions?.find(
  (action) => ['pending', 'claimed'].includes(String(action.status || '').toLowerCase()),
);

const beginnerBotState = (bot, dryRunState, paperState) => {
  const pending = activePendingAction(paperState);
  if (pending) {
    const action = String(pending.action_type || '').toLowerCase();
    if (pending.proposal_status === 'PENDING') {
      if (action === 'enter_short') return 'Open short suggestion awaiting your approval';
      if (action === 'exit_short') return 'Cover short suggestion awaiting your approval';
      return action === 'exit_long'
        ? 'Sell suggestion awaiting your approval'
        : 'Buy suggestion awaiting your approval';
    }
    if (action === 'enter_short') return 'Short entry pending for next market open';
    if (action === 'exit_short') return 'Short cover pending for next market open';
    return action === 'exit_long'
      ? 'Exit pending for next market open'
      : 'Entry pending for next market open';
  }
  if (paperState?.positions?.length) return 'Position open';
  return presentBotReadiness(bot, dryRunState).label;
};

const customerErrorMessage = customerBotCreationError;

const pickResultFromSession = (session, symbols) => {
  if (!session || ![5, 10, 20].includes(Number(session.requestedCount))) return null;
  const selectedCompanies = Array.isArray(session.selectedCompanies) ? session.selectedCompanies : [];
  return {
    requestedCount: Number(session.requestedCount),
    selectedCount: symbols.length,
    stockFocusMatchCount: Number(session.stockFocusMatchCount)
      || Number(session.eligibleCount) || 0,
    eligibleCount: Number(session.eligibleCount) || 0,
    stockFocus: session.stockFocus || null,
    keptSymbols: Array.isArray(session.keptSymbols) ? session.keptSymbols : [],
    selectedCompanies,
  };
};

const pickSessionForDraft = (result) => (result ? {
  requestedCount: result.requestedCount,
  stockFocusMatchCount: result.stockFocusMatchCount,
  eligibleCount: result.eligibleCount,
  stockFocus: result.stockFocus,
  keptSymbols: result.keptSymbols,
  selectedCompanies: result.selectedCompanies,
} : null);

const orderPickCompanies = (resultSymbols, priorSymbols, keptSymbols, requestedCount) => {
  const companies = (Array.isArray(resultSymbols) ? resultSymbols : [])
    .map((item) => ({
      symbol: String(item?.symbol || '').trim().toUpperCase(),
      company_name: item?.company_name || null,
      kept: item?.kept === true,
    }))
    .filter((item) => /^[A-Z][A-Z0-9.-]{0,14}$/.test(item.symbol));
  const kept = new Map(companies.filter((item) => item.kept).map((item) => [item.symbol, item]));
  const rolled = companies.filter((item) => !item.kept);
  const visual = [];
  const used = new Set();
  let rolledIndex = 0;
  priorSymbols.slice(0, requestedCount).forEach((symbol) => {
    if (keptSymbols.includes(symbol) && kept.has(symbol)) {
      visual.push(kept.get(symbol));
      used.add(symbol);
    } else if (rolled[rolledIndex]) {
      visual.push(rolled[rolledIndex]);
      used.add(rolled[rolledIndex].symbol);
      rolledIndex += 1;
    }
  });
  companies.forEach((item) => {
    if (!used.has(item.symbol)) visual.push(item);
  });
  return visual;
};

const BotsPage = () => {
  const { token, isTokenVerified } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const simpleSetupOwner = simpleBotSetupOwnerFromToken(token);
  const [initialSimpleDraft] = useState(() => readSimpleBotSetup(simpleSetupOwner));
  const queryEnabled = Boolean(token && isTokenVerified);
  const [editingBotId, setEditingBotId] = useState(null);
  const [creationMode, setCreationMode] = useState('simple');
  const [botName, setBotName] = useState(initialSimpleDraft.name);
  const [botNameSource, setBotNameSource] = useState(initialSimpleDraft.nameSource);
  const [stockDiscoveryMode, setStockDiscoveryMode] = useState(initialSimpleDraft.discoveryMode);
  const [botTemplate, setBotTemplate] = useState(initialSimpleDraft.preset);
  const [botTradingStrategy, setBotTradingStrategy] = useState(
    initialSimpleDraft.tradingStrategy,
  );
  const [justSavedPercentageStrategy, setJustSavedPercentageStrategy] = useState(null);
  const [advancedFactoryStrategy, setAdvancedFactoryStrategy] = useState('');
  const [advancedStrategyDefinition, setAdvancedStrategyDefinition] = useState('');
  const [advancedStrategyName, setAdvancedStrategyName] = useState('My custom strategy');
  const [advancedScreeningDefinitionId, setAdvancedScreeningDefinitionId] = useState('');
  const [editingEvaluatorVersion, setEditingEvaluatorVersion] = useState(null);
  const [editingOriginalStrategy, setEditingOriginalStrategy] = useState(null);
  const [revisionConfirmation, setRevisionConfirmation] = useState(null);
  const [riskBudget, setRiskBudget] = useState(initialSimpleDraft.maximumAllocation);
  const [botSymbols, setBotSymbols] = useState(initialSimpleDraft.symbols.join(', '));
  const [screeningLineage, setScreeningLineage] = useState(
    initialSimpleDraft.screeningLineage,
  );
  const [selectionMethod, setSelectionMethod] = useState(initialSimpleDraft.selectionMethod);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickResult, setPickResult] = useState(() => pickResultFromSession(
    initialSimpleDraft.pickSession, initialSimpleDraft.symbols,
  ));
  const [pickUndoSnapshot, setPickUndoSnapshot] = useState(null);
  const [companyContextStock, setCompanyContextStock] = useState(null);
  const companyContextReturnFocus = useRef(null);
  const [newsSentimentEnabled, setNewsSentimentEnabled] = useState(initialSimpleDraft.newsSentimentEnabled);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [savingBot, setSavingBot] = useState(false);
  const [dryRunStates, setDryRunStates] = useState({});
  const [workingDryRunActions, setWorkingDryRunActions] = useState({});
  const [startErrors, setStartErrors] = useState({});
  const [botRuntimeFeedback, setBotRuntimeFeedback] = useState({});
  const [startFeedbackToReveal, setStartFeedbackToReveal] = useState(null);
  const dryRunRequestsInFlight = useRef(new Set());
  const [paperStates, setPaperStates] = useState({});
  const [workingPaperActions, setWorkingPaperActions] = useState({});
  const [botPaperFeedback, setBotPaperFeedback] = useState({});
  const paperRequestsInFlight = useRef(new Set());
  const [prepareConfirmation, setPrepareConfirmation] = useState(null);
  const [prepareConfirmationError, setPrepareConfirmationError] = useState('');
  const [workingPrepareBotId, setWorkingPrepareBotId] = useState(null);
  const [botActionNotice, setBotActionNotice] = useState(null);
  const prepareRequestInFlight = useRef(false);
  const prepareDialogRef = useRef(null);
  const prepareConfirmButtonRef = useRef(null);
  const prepareTriggerRef = useRef(null);
  const revisionDialogRef = useRef(null);
  const revisionConfirmButtonRef = useRef(null);
  const revisionTriggerRef = useRef(null);
  const revisionRequestInFlight = useRef(false);
  const creationRequestInFlight = useRef(false);
  const botListRegionRef = useRef(null);
  const creationSectionRef = useRef(null);
  const percentageComparisonTargetRef = useRef(null);
  const protectiveComparisonTargetRef = useRef(null);
  const savedProtectiveTargetRef = useRef(null);
  const botNameInputRef = useRef(null);
  const creationFocusRequestedRef = useRef(false);
  const focusedBotHashRef = useRef('');
  const [searchParams] = useSearchParams();
  const percentageSetupHandoff = searchParams.get('create') === 'percentage';
  const stockSetupHandoff = searchParams.get('create') === 'stock';
  const [simpleStep, setSimpleStep] = useState(() => {
    const requested = Number(searchParams.get('step'));
    return Number.isInteger(requested) && requested >= 1 && requested <= 5 ? requested : 1;
  });
  const [showCreator, setShowCreator] = useState(false);
  const [createdBotId, setCreatedBotId] = useState(null);
  const [shortStrategyAcknowledged, setShortStrategyAcknowledged] = useState(false);
  const [candidateNotice, setCandidateNotice] = useState('');
  const [comparisonProtectiveId, setComparisonProtectiveId] = useState('example');
  const [percentageSetupEpoch, setPercentageSetupEpoch] = useState(0);
  const [comparisonHandoff, setComparisonHandoff] = useState(null);
  const comparisonHandoffRef = useRef(null);
  useEffect(() => {
    if (!percentageSetupHandoff && !stockSetupHandoff) return;
    creationFocusRequestedRef.current = true;
    setShowCreator(true);
  }, [percentageSetupHandoff, stockSetupHandoff]);

  const templatesQuery = useQuery({
    queryKey: ['supported-bot-presets'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/bots/presets');
      return response.data || {};
    },
    enabled: queryEnabled,
  });

  const factoryStrategiesQuery = useQuery({
    queryKey: ['factory-trading-strategies', simpleSetupOwner],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/bots/trading-strategies');
      return response.data || {};
    },
    enabled: queryEnabled,
    retry: false,
  });

  const customStrategyTemplateQuery = useQuery({
    queryKey: ['modern-custom-strategy-template'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/bots/custom-strategy-template');
      return response.data;
    },
    enabled: queryEnabled,
    staleTime: Infinity,
  });

  const customStrategiesQuery = useQuery({
    queryKey: ['owned-custom-strategies', simpleSetupOwner],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/strategy-definitions');
      return response.data?.strategies || [];
    },
    enabled: queryEnabled,
    retry: false,
  });

  const advancedScreeningQuery = useQuery({
    queryKey: ['custom-screening-definitions'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/screening/definitions');
      return response.data?.definitions || [];
    },
    enabled: queryEnabled,
  });

  const botsQuery = useQuery({
    queryKey: ['draft-bots'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/bots');
      return response.data || [];
    },
    enabled: queryEnabled,
    refetchInterval: 15000,
  });

  const liveActivityQuery = useBotLiveActivity(
    queryEnabled, null, botsQuery.data?.map((bot) => bot.id) || [],
  );

  const ownedBotsForNamingQuery = useQuery({
    queryKey: ['owned-bots-for-default-name', simpleSetupOwner],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/bots?include_archived=true');
      return response.data || [];
    },
    enabled: queryEnabled,
    staleTime: 15000,
  });

  const oauthQuery = useQuery({
    queryKey: ['alpaca-market-data-status'],
    queryFn: async () => (await axiosInstance.get('/api/alpaca/oauth/status')).data,
    enabled: queryEnabled,
    retry: false,
  });
  const premiumEntitlementQuery = usePremiumEntitlement(queryEnabled);

  const templateEntries = Object.entries(templatesQuery.data || {})
    .filter(([name]) => name !== 'Custom Template');
  const customScreeningDefinitions = useMemo(
    () => advancedScreeningQuery.data || [],
    [advancedScreeningQuery.data],
  );
  const selectedCustomScreening = customScreeningDefinitions.find(
    (definition) => String(definition.id) === String(advancedScreeningDefinitionId),
  );
  const factoryStrategies = factoryStrategiesQuery.data?.strategies || [];
  const customizableFactoryStrategies = factoryStrategies.filter((strategy) => (
    !strategy.experimental && strategy.presentation?.advanced_customization_available !== false
  ));
  const selectedFactoryStrategy = factoryStrategies.find(
    (strategy) => strategy.slug === botTradingStrategy,
  );
  const customStrategies = justSavedPercentageStrategy
    && !(customStrategiesQuery.data || []).some((strategy) => strategy.id === justSavedPercentageStrategy.id)
    ? [...(customStrategiesQuery.data || []), justSavedPercentageStrategy]
    : customStrategiesQuery.data || [];
  const selectedCustomStrategy = customStrategies.find(
    (strategy) => `custom:${strategy.id}` === botTradingStrategy,
  );
  const selectedProtectiveSettings = protectiveSettingsFromDefinition(selectedCustomStrategy);
  const selectedProtectiveEntryName = selectedProtectiveSettings
    ? strategyDisplayName(selectedProtectiveSettings.sourceFamily,
      factoryStrategies.find((strategy) => strategy.slug === selectedProtectiveSettings.sourceFamily)?.name
      || selectedProtectiveSettings.sourceFamily)
    : null;
  const percentageSelected = botTradingStrategy === PERCENTAGE_PRESET
    || Boolean(percentageSettingsFromDefinition(selectedCustomStrategy));
  const savedPercentageStrategies = customStrategies.filter(percentageSettingsFromDefinition);
  const percentageExample = percentageComparisonDetails();
  const savedProtectiveStrategies = customStrategies.filter(protectiveSettingsFromDefinition);
  const comparisonProtectiveStrategy = savedProtectiveStrategies.find(
    (strategy) => strategy.id === comparisonProtectiveId,
  );
  const comparisonProtectiveExits = comparisonProtectiveId === 'example'
    ? { loss_guard: '5', profit_target: '8', profit_trail: '5' }
    : protectiveSettingsFromDefinition(comparisonProtectiveStrategy);
  const selectedProfileGuidance = factoryStrategiesQuery.data
    ?.profile_guidance?.[botTemplate]?.[botTradingStrategy];
  const activeTemplateEntries = templateEntries;
  const oauthConnected = oauthQuery.data?.connected === true;
  const oauthMissing = oauthQuery.isSuccess && !oauthConnected;
  const firstBotJourney = !botsQuery.isLoading && (botsQuery.data || []).length === 0;
  const welcomedFromAlpaca = searchParams.get('welcome') === 'connected';
  const premiumBacktesting = premiumEntitlementQuery.isSuccess
    && isPremiumPlan(premiumEntitlementQuery.data);
  const nonPremiumBacktesting = premiumEntitlementQuery.isSuccess && !premiumBacktesting;

  useEffect(() => {
    if (
      editingBotId
      || creationMode !== 'simple'
      || botNameSource !== 'suggested'
      || !ownedBotsForNamingQuery.isSuccess
    ) return;
    const suggestion = nextDefaultBotName(ownedBotsForNamingQuery.data);
    if (botName !== suggestion) setBotName(suggestion);
  }, [
    botName,
    botNameSource,
    creationMode,
    editingBotId,
    ownedBotsForNamingQuery.data,
    ownedBotsForNamingQuery.isSuccess,
  ]);

  useEffect(() => {
    if (!firstBotJourney || !welcomedFromAlpaca || window.location.hash !== '#hire') return;
    window.requestAnimationFrame(() => {
      document.getElementById('hire')?.scrollIntoView({ block: 'start' });
    });
  }, [firstBotJourney, welcomedFromAlpaca]);

  useEffect(() => {
    if (!showCreator || !creationFocusRequestedRef.current) return undefined;
    creationFocusRequestedRef.current = false;
    return startBotCreatorScrollHandoff({
      contentAbove: botListRegionRef.current,
      input: botNameInputRef.current,
      section: creationSectionRef.current,
    });
  }, [showCreator]);

  useEffect(() => {
    if (!showCreator || simpleStep !== 2 || !comparisonHandoff) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const target = comparisonHandoff.kind === 'percentage'
        ? percentageComparisonTargetRef.current
        : comparisonHandoff.kind === 'protective'
          ? savedProtectiveTargetRef.current || protectiveComparisonTargetRef.current
          : comparisonHandoffRef.current;
      target?.scrollIntoView({ block: 'center' });
      target?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [showCreator, simpleStep, comparisonHandoff, selectedCustomStrategy?.id]);

  useEffect(() => {
    const match = location.hash.match(/^#bot-(\d+)$/);
    if (!match) {
      focusedBotHashRef.current = '';
      return undefined;
    }
    if (
      !botsQuery.isSuccess
      || focusedBotHashRef.current === location.hash
    ) return undefined;
    const botId = Number(match[1]);
    if (!(botsQuery.data || []).some((bot) => bot.id === botId)) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`bot-${botId}`);
      target?.scrollIntoView({ block: 'center' });
      target?.focus({ preventScroll: true });
      focusedBotHashRef.current = location.hash;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [botsQuery.data, botsQuery.isSuccess, location.hash]);

  useEffect(() => {
    if (!prepareConfirmation) return undefined;
    prepareConfirmButtonRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !prepareRequestInFlight.current) {
        setPrepareConfirmation(null);
        setPrepareConfirmationError('');
        window.requestAnimationFrame(() => prepareTriggerRef.current?.focus());
      }
      if (event.key === 'Tab') {
        const controls = [...(prepareDialogRef.current?.querySelectorAll('button:not(:disabled)') || [])];
        if (!controls.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [prepareConfirmation]);

  useEffect(() => {
    if (!revisionConfirmation) return undefined;
    revisionConfirmButtonRef.current?.focus();
    const handleDialogKey = (event) => {
      if (event.key === 'Escape' && !savingBot) {
        setRevisionConfirmation(null);
        window.requestAnimationFrame(() => revisionTriggerRef.current?.focus());
      }
      if (event.key === 'Tab') {
        const controls = [...(revisionDialogRef.current?.querySelectorAll('button:not(:disabled)') || [])];
        if (!controls.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleDialogKey);
    return () => document.removeEventListener('keydown', handleDialogKey);
  }, [revisionConfirmation, savingBot]);

  useEffect(() => {
    const requested = searchParams.get('customScreening');
    if (!editingBotId && requested && customScreeningDefinitions.some((item) => String(item.id) === requested)) {
      setCreationMode('advanced');
      setAdvancedScreeningDefinitionId(requested);
    }
  }, [customScreeningDefinitions, editingBotId, searchParams]);

  useEffect(() => {
    if (
      creationMode === 'advanced'
      && !editingBotId
      && !advancedFactoryStrategy
      && !advancedStrategyDefinition
      && customStrategyTemplateQuery.data?.strategy_configuration
    ) {
      setAdvancedStrategyName(customStrategyTemplateQuery.data.strategy_name);
      setAdvancedStrategyDefinition(JSON.stringify(
        customStrategyTemplateQuery.data.strategy_configuration, null, 2,
      ));
    }
  }, [creationMode, editingBotId, advancedFactoryStrategy, advancedStrategyDefinition, customStrategyTemplateQuery.data]);

  useEffect(() => {
    if (editingBotId || creationMode !== 'simple') return;
    writeSimpleBotSetup({
      name: botName,
      nameSource: botNameSource,
      discoveryMode: stockDiscoveryMode,
      preset: botTemplate,
      tradingStrategy: botTradingStrategy,
      symbols: parseSymbolInput(botSymbols),
      screeningLineage,
      selectionMethod,
      pickSession: pickSessionForDraft(pickResult),
      maximumAllocation: riskBudget,
      newsSentimentEnabled,
    }, simpleSetupOwner);
  }, [botName, botNameSource, stockDiscoveryMode, botTemplate, botTradingStrategy, botSymbols, screeningLineage, selectionMethod, pickResult, riskBudget, newsSentimentEnabled, creationMode, editingBotId, simpleSetupOwner]);

  useEffect(() => {
    const readyBots = (botsQuery.data || []).filter((bot) => bot.status === 'ready');
    if (!queryEnabled || readyBots.length === 0) return undefined;
    let active = true;

    const refresh = async () => {
      const results = await Promise.all(readyBots.map(async (bot) => {
        try {
          const response = await axiosInstance.get(`/api/bots/${bot.id}/runtime/dry-run`);
          return [bot.id, response.data];
        } catch (error) {
          if (error.response?.status === 404) return [bot.id, null];
          return [bot.id, { error: 'Practice status unavailable.' }];
        }
      }));
      if (active) {
        setDryRunStates((current) => {
          const next = { ...current };
          results.forEach(([botId, state]) => {
            if (state) {
              const priorGeneration = Number(next[botId]?.generation);
              const receivedGeneration = Number(state.generation);
              if (!Number.isFinite(priorGeneration) || !Number.isFinite(receivedGeneration)
                || receivedGeneration >= priorGeneration) next[botId] = state;
            }
            else if (!next[botId]?.generation) delete next[botId];
          });
          return next;
        });
      }
    };

    refresh();
    const timer = window.setInterval(refresh, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [botsQuery.data, queryEnabled]);

  useEffect(() => {
    if (startFeedbackToReveal === null) return;
    document.getElementById(`bot-${startFeedbackToReveal.botId}-start-feedback`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [startFeedbackToReveal, workingDryRunActions]);

  useEffect(() => {
    const readyBots = (botsQuery.data || []).filter((bot) => bot.status === 'ready');
    if (!queryEnabled || readyBots.length === 0) return undefined;
    let active = true;
    const refresh = async () => {
      const results = await Promise.all(readyBots.map(async (bot) => {
        try {
          const response = await axiosInstance.get(`/api/bots/${bot.id}/runtime/paper`);
          return [bot.id, response.data];
        } catch (error) {
          if (error.response?.status === 404) return [bot.id, null];
          return [bot.id, { error: 'Virtual portfolio unavailable.' }];
        }
      }));
      if (active) {
        setPaperStates((current) => {
          const next = { ...current };
          results.forEach(([botId, state]) => {
            if (state) next[botId] = state;
            else delete next[botId];
          });
          return next;
        });
      }
    };
    refresh();
    const timer = window.setInterval(refresh, 10000);
    return () => { active = false; window.clearInterval(timer); };
  }, [botsQuery.data, queryEnabled]);

  const restoreSimpleForm = () => {
    const draft = readSimpleBotSetup(simpleSetupOwner);
    setBotName(draft.name);
    setBotNameSource(draft.nameSource);
    setStockDiscoveryMode(draft.discoveryMode);
    setBotTemplate(draft.preset);
    setBotTradingStrategy(draft.tradingStrategy);
    setRiskBudget(draft.maximumAllocation);
    setBotSymbols(draft.symbols.join(', '));
    setScreeningLineage(draft.screeningLineage);
    setSelectionMethod(draft.selectionMethod);
    setNewsSentimentEnabled(draft.newsSentimentEnabled);
  };

  const openBotCreator = () => {
    if (showCreator || editingBotId) return;
    creationFocusRequestedRef.current = true;
    setCreatedBotId(null);
    setShowCreator(true);
  };

  const resetBotForm = ({ clearSimple = false } = {}) => {
    if (clearSimple) clearSimpleBotSetup();
    setEditingBotId(null);
    setEditingEvaluatorVersion(null);
    setEditingOriginalStrategy(null);
    setRevisionConfirmation(null);
    setAdvancedScreeningDefinitionId('');
    setCreationMode('simple');
    if (clearSimple) {
      setBotName(EMPTY_SIMPLE_BOT_SETUP.name);
      setBotNameSource('suggested');
      setStockDiscoveryMode('STOCK_FOCUS');
      setBotTemplate('Blue-Chip Stability');
      setBotTradingStrategy('trend-pullback');
      setRiskBudget('10');
      setBotSymbols('');
      setScreeningLineage(null);
      setSelectionMethod(null);
      setNewsSentimentEnabled(false);
    } else {
      restoreSimpleForm();
    }
    setFormError('');
    setFormSuccess('');
    setSimpleStep(1);
    setShortStrategyAcknowledged(false);
  };

  const selectComparedStrategy = (slug, name, kind = 'strategy') => {
    if (editingBotId) resetBotForm();
    setCreationMode('simple');
    setBotTradingStrategy(slug);
    setShortStrategyAcknowledged(false);
    setFormError('');
    setFormSuccess('');
    setCreatedBotId(null);
    if (slug !== botTradingStrategy && (slug === PERCENTAGE_PRESET || slug.startsWith('custom:'))) {
      setPercentageSetupEpoch((value) => value + 1);
    }
    setComparisonHandoff({ slug, name, kind });
    setShowCreator(true);
    setSimpleStep(2);
  };

  const editBot = async (bot) => {
    setEditingBotId(bot.id);
    setEditingEvaluatorVersion(bot.evaluator_version || null);
    setCreationMode('advanced');
    setBotName(bot.name);
    setBotNameSource('user');
    setBotTemplate(bot.template_name);
    setBotTradingStrategy('');
    setAdvancedFactoryStrategy('');
    setAdvancedStrategyDefinition('');
    setEditingOriginalStrategy(null);
    setRevisionConfirmation(null);
    setAdvancedStrategyName(bot.trading_strategy_name || 'My custom strategy');
    setAdvancedScreeningDefinitionId(bot.custom_screening_definition_id || '');
    setRiskBudget(String(bot.risk_budget_pct));
    setBotSymbols((bot.symbols || []).join(', '));
    setScreeningLineage(null);
    setSelectionMethod(null);
    setNewsSentimentEnabled(Boolean(bot.trading_config?.news_sentiment_enabled));
    setFormError('');
    setFormSuccess('');
    if (
      isEditableCustomEvaluator(bot.evaluator_version)
      && bot.strategy_definition_id
    ) {
      try {
        const response = await axiosInstance.get(
          `/api/strategy-definitions/${bot.strategy_definition_id}`,
        );
        setAdvancedStrategyName(response.data.strategy_name);
        setEditingOriginalStrategy({
          canonical: JSON.stringify(response.data.canonical_configuration),
          name: response.data.strategy_name,
        });
        setAdvancedStrategyDefinition(JSON.stringify(
          response.data.canonical_configuration, null, 2,
        ));
      } catch {
        setFormError('This saved strategy could not be loaded. Nothing was changed.');
      }
    }
  };

  const persistBot = async ({ payload, symbols, strategyConfiguration, shouldRevise }) => {
    setSavingBot(true);
    setFormError('');
    setFormSuccess('');
    try {
      let savedBot = null;
      if (editingBotId) {
        if (shouldRevise) {
          await axiosInstance.post(`/api/bots/${editingBotId}/strategy-definitions`, {
            strategy_name: advancedStrategyName.trim(),
            strategy_configuration: strategyConfiguration,
          });
        }
        await axiosInstance.patch(`/api/bots/${editingBotId}`, payload);
        await axiosInstance.put(`/api/bots/${editingBotId}/symbols`, { symbols });
      } else if (creationMode === 'simple' || advancedFactoryStrategy) {
        const response = await axiosInstance.post('/api/bots/preset', { ...payload, symbols });
        savedBot = response.data;
      } else {
        const response = await axiosInstance.post('/api/bots', payload);
        await axiosInstance.put(`/api/bots/${response.data.id}/symbols`, { symbols });
      }
      const successMessage = editingBotId
        ? 'Your draft bot changes were saved.'
        : creationMode === 'simple' || advancedFactoryStrategy
          ? 'Your bot has been created.'
          : 'Your advanced bot draft was saved. Prepare its market data when the configuration is complete.';
      resetBotForm({ clearSimple: !editingBotId && creationMode === 'simple' });
      setFormSuccess(successMessage);
      await Promise.all([botsQuery.refetch(), ownedBotsForNamingQuery.refetch()]);
      if (savedBot?.id && creationMode === 'simple') {
        setCreatedBotId(savedBot.id);
        setShowCreator(false);
        navigate(`/bots#bot-${savedBot.id}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = customerErrorMessage(
        detail,
        creationMode === 'simple'
          ? 'We couldn’t create this bot. Your choices are still here. Please try again.'
          : 'Unable to save this advanced bot draft.',
      );
      if (
        !editingBotId
        && creationMode === 'simple'
        && isBotStructuralEligibilityError(detail)
      ) {
        setSimpleStep(3);
        setCandidateNotice(message);
        setFormError(`${message} Your other bot choices are still here.`);
        window.requestAnimationFrame(() => {
          const target = document.getElementById('bot-symbols');
          target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          target?.focus();
        });
      } else {
        setFormError(message);
      }
      await botsQuery.refetch();
    } finally {
      setSavingBot(false);
    }
  };

  const saveBot = async (event) => {
    event.preventDefault();
    if (savingBot || creationRequestInFlight.current) return;
    setFormError('');
    setFormSuccess('');
    if (creationMode === 'simple' && !selectedFactoryStrategy && !selectedCustomStrategy) {
      setSimpleStep(2);
      setFormError('Save your Percentage Trading rules or choose another strategy before creating a bot.');
      return;
    }
    const symbols = botSymbols
      .split(',')
      .map((symbol) => symbol.trim())
      .filter(Boolean);
    const payload = {
      name: botName.trim(),
      template_name: creationMode === 'advanced'
        ? (selectedCustomScreening?.name || '')
        : stockDiscoveryMode === 'ALL_STOCKS' ? 'All Stocks' : botTemplate,
      risk_budget_pct: Number(riskBudget),
      news_sentiment_enabled: newsSentimentEnabled,
    };
    if (creationMode === 'advanced') {
      if (!advancedScreeningDefinitionId) {
        setFormError('Choose or create your saved screening rules.');
        setSavingBot(false);
        return;
      }
      payload.custom_screening_definition_id = advancedScreeningDefinitionId;
    }
    if (creationMode === 'simple' && botTradingStrategy) {
      if (selectedCustomStrategy) {
        payload.strategy_definition_id = selectedCustomStrategy.id;
      } else {
        payload.trading_strategy_slug = botTradingStrategy;
      }
      payload.stock_selection_mode = stockDiscoveryMode;
      payload.short_strategy_acknowledged = botTradingStrategy === 'the-big-short'
        && shortStrategyAcknowledged;
      payload.news_sentiment_enabled = false;
      if (screeningLineage && screeningLineage.profileId === botTemplate) {
        payload.stock_focus_selection = {
          generation_id: screeningLineage.generationId,
          generation_hash: screeningLineage.generationHash,
          profile_id: screeningLineage.profileId,
          profile_schema_version: screeningLineage.profileSchemaVersion,
          profile_bundle_hash: screeningLineage.profileBundleHash,
        };
      }
    }
    if (creationMode === 'advanced' && advancedFactoryStrategy) {
      payload.trading_strategy_slug = advancedFactoryStrategy;
      payload.news_sentiment_enabled = false;
      try {
        payload.trading_strategy_configuration = JSON.parse(advancedStrategyDefinition);
      } catch {
        setFormError('The customized technical definition must be valid JSON.');
        return;
      }
    }
    if (creationMode === 'advanced' && !advancedFactoryStrategy && !editingBotId) {
      payload.strategy_name = advancedStrategyName.trim();
      payload.news_sentiment_enabled = false;
      try {
        payload.strategy_configuration = JSON.parse(advancedStrategyDefinition);
      } catch {
        setFormError('The expert strategy definition must be valid JSON.');
        return;
      }
    }

    let strategyConfiguration = null;
    let shouldRevise = false;
    if (editingBotId && isEditableCustomEvaluator(editingEvaluatorVersion)) {
      try {
        strategyConfiguration = JSON.parse(advancedStrategyDefinition);
      } catch {
        setFormError('The expert strategy definition must be valid JSON.');
        return;
      }
      const holding = maximumHoldingState(strategyConfiguration);
      const holdingError = validateMaximumHolding(holding);
      if (holdingError) {
        setFormError(holdingError);
        document.getElementById('maximum-holding-sessions')?.focus();
        return;
      }
      shouldRevise = !editingOriginalStrategy
        || editingOriginalStrategy.canonical !== JSON.stringify(strategyConfiguration)
        || editingOriginalStrategy.name !== advancedStrategyName.trim();
      if (shouldRevise) {
        setRevisionConfirmation({
          payload,
          symbols,
          strategyConfiguration,
          shouldRevise,
          summary: maximumHoldingReview(strategyConfiguration),
        });
        return;
      }
    }
    if (!editingBotId) creationRequestInFlight.current = true;
    try {
      await persistBot({ payload, symbols, strategyConfiguration, shouldRevise });
    } finally {
      creationRequestInFlight.current = false;
    }
  };

  const confirmStrategyRevision = async () => {
    if (!revisionConfirmation || savingBot || revisionRequestInFlight.current) return;
    const pending = revisionConfirmation;
    revisionRequestInFlight.current = true;
    setRevisionConfirmation(null);
    try {
      await persistBot(pending);
    } finally {
      revisionRequestInFlight.current = false;
    }
  };

  const simpleSymbols = parseSymbolInput(botSymbols);
  const pickKeptCount = (pickResult?.keptSymbols || []).filter((symbol) => simpleSymbols.includes(symbol)).length;
  const pickFinalCount = pickResult
    ? Math.min(Number(pickResult.requestedCount), Number(pickResult.eligibleCount))
    : 0;
  const pickRollLabel = pickResult
    ? pickAnotherLabel(Number(pickResult.requestedCount), pickKeptCount, pickFinalCount)
    : null;
  const pickForMe = async (count) => {
    if (pickLoading || stockDiscoveryMode !== 'STOCK_FOCUS' || !botTemplate) return;
    const keptSymbols = (pickResult?.keptSymbols || []).filter((symbol) => simpleSymbols.includes(symbol));
    if (keptSymbols.length > count) {
      setCandidateNotice(`You’ve kept ${keptSymbols.length} stocks. Unkeep or remove enough companies before choosing Pick ${count}.`);
      return;
    }
    const currentSelectionWasManuallyEdited = !pickResult && simpleSymbols.length > 0 && selectionMethod !== 'RANDOM_STOCK_FOCUS';
    if (currentSelectionWasManuallyEdited && !window.confirm('Pick another set will replace your current stock selection.')) return;

    setPickLoading(true);
    setCandidateNotice('');
    try {
      const response = await axiosInstance.post('/api/stocks/stock-focus/pick', {
        stock_focus: botTemplate,
        count,
        kept_symbols: keptSymbols,
      });
      const result = response.data;
      const selectedCompanies = orderPickCompanies(
        result?.symbols, simpleSymbols, keptSymbols, Number(result?.requested_count),
      );
      const symbols = selectedCompanies.map((item) => item.symbol);
      const lineage = stockFocusSelectionLineage(result?.generation, result?.stock_focus);
      if (
        result?.stock_focus !== botTemplate
        || !lineage
        || symbols.length !== Number(result?.selected_count)
        || !Number.isInteger(Number(result?.stock_focus_match_count))
        || Number(result?.stock_focus_match_count) < Number(result?.eligible_count)
      ) {
        throw new Error('The Stock Focus candidate list changed. Pick again or review your stocks before creating the bot.');
      }
      setPickUndoSnapshot({
        symbols: botSymbols,
        lineage: screeningLineage,
        selectionMethod,
        pickResult,
      });
      setBotSymbols(symbols.join(', '));
      setScreeningLineage(lineage);
      setSelectionMethod('RANDOM_STOCK_FOCUS');
      setPickResult({
        requestedCount: Number(result.requested_count),
        selectedCount: symbols.length,
        stockFocusMatchCount: Number(result.stock_focus_match_count),
        eligibleCount: Number(result.eligible_count),
        stockFocus: result.stock_focus,
        keptSymbols: selectedCompanies.filter((item) => item.kept).map((item) => item.symbol),
        selectedCompanies,
      });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setCandidateNotice(
        (typeof detail === 'string' ? detail : detail?.message)
        || error?.message
        || 'Pinngro could not choose a random Stock Focus selection. Your current choices have not changed.',
      );
    } finally {
      setPickLoading(false);
    }
  };
  const openCompanyContext = (stock, trigger) => {
    companyContextReturnFocus.current = trigger;
    setCompanyContextStock(stock);
  };
  const closeCompanyContext = () => {
    setCompanyContextStock(null);
    window.setTimeout(() => companyContextReturnFocus.current?.focus(), 0);
  };
  const removePickedStock = (symbol) => {
    const normalized = String(symbol || '').trim().toUpperCase();
    setBotSymbols(simpleSymbols.filter((item) => item !== normalized).join(', '));
    setPickResult((current) => (current ? {
      ...current,
      selectedCompanies: (current.selectedCompanies || []).filter((item) => item.symbol !== normalized),
      keptSymbols: (current.keptSymbols || []).filter((item) => item !== normalized),
      selectedCount: Math.max(Number(current.selectedCount) - 1, 0),
    } : current));
    setCandidateNotice(`${normalized} removed. Pick another set to return to your selected basket size.`);
  };

  const togglePickedKeep = (symbol) => {
    const normalized = String(symbol || '').trim().toUpperCase();
    setPickResult((current) => {
      if (!current) return current;
      const kept = new Set(current.keptSymbols || []);
      if (kept.has(normalized)) kept.delete(normalized); else kept.add(normalized);
      return { ...current, keptSymbols: Array.from(kept) };
    });
  };

  const undoPickForMe = () => {
    if (!pickUndoSnapshot) return;
    setBotSymbols(pickUndoSnapshot.symbols);
    setScreeningLineage(pickUndoSnapshot.lineage);
    setSelectionMethod(pickUndoSnapshot.selectionMethod);
    setPickResult(pickUndoSnapshot.pickResult || null);
    setPickUndoSnapshot(null);
    setCandidateNotice('Your previous stock selection was restored.');
  };
  const changeStockDiscoveryMode = (mode) => {
    if (mode === stockDiscoveryMode) return;
    setStockDiscoveryMode(mode);
    setBotSymbols('');
    setScreeningLineage(null);
    setSelectionMethod(null);
    setPickResult(null);
    setPickUndoSnapshot(null);
    setCandidateNotice('');
  };
  const simpleStepComplete = [
    Boolean(botName.trim().length >= 3 && (stockDiscoveryMode === 'ALL_STOCKS' || botTemplate)),
    Boolean(selectedFactoryStrategy || selectedCustomStrategy),
    simpleSymbols.length > 0,
    Number(riskBudget) > 0 && Number(riskBudget) <= 100,
    Boolean((stockDiscoveryMode === 'ALL_STOCKS' || botTemplate) && (selectedFactoryStrategy || selectedCustomStrategy) && simpleSymbols.length && Number(riskBudget) > 0
      && (botTradingStrategy !== 'the-big-short' || shortStrategyAcknowledged)),
  ];
  const canOpenSimpleStep = (index) => index === 0 || simpleStepComplete.slice(0, index).every(Boolean);
  const simpleStepState = (index) => {
    if (index + 1 === simpleStep) return 'current';
    if (index + 1 < simpleStep && simpleStepComplete[index]) return 'completed';
    return 'future';
  };
  const SetupSoFar = ({ includeStrategy = false, includeStocks = false }) => (
    <aside className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300" aria-label="Your setup so far">
      <p className="font-black uppercase tracking-wide text-slate-500">Your setup so far</p>
      <p><strong>Find stocks:</strong> {stockDiscoveryMode === 'ALL_STOCKS' ? 'All Stocks' : botTemplate || 'Stock Focus not chosen'}</p>
      {includeStrategy && <p><strong>Trading Strategy:</strong> {selectedFactoryStrategy?.name || selectedCustomStrategy?.strategy_name || 'Not chosen'}</p>}
      {includeStocks && <p><strong>Stocks:</strong> {simpleSymbols.length ? `${simpleSymbols.join(', ')} (${simpleSymbols.length} selected)` : 'None selected'}</p>}
    </aside>
  );

  const archiveBot = async (bot) => {
    if (!window.confirm(`Archive "${bot.name}"? Its saved configuration will be retained.`)) {
      return;
    }
    try {
      await axiosInstance.delete(`/api/bots/${bot.id}`, { data: {
        reason: 'Owner archived the draft bot from Your Bots',
        idempotency_key: crypto.randomUUID(),
      } });
      if (editingBotId === bot.id) resetBotForm();
      await botsQuery.refetch();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Unable to archive this bot.');
    }
  };

  const openPrepareConfirmation = (bot, trigger) => {
    prepareTriggerRef.current = trigger;
    setPrepareConfirmation({ id: bot.id, name: bot.name });
    setPrepareConfirmationError('');
    setBotActionNotice(null);
  };

  const closePrepareConfirmation = () => {
    if (prepareRequestInFlight.current) return;
    setPrepareConfirmation(null);
    setPrepareConfirmationError('');
    window.requestAnimationFrame(() => prepareTriggerRef.current?.focus());
  };

  const promoteBot = async () => {
    if (!prepareConfirmation || prepareRequestInFlight.current) return;
    const bot = prepareConfirmation;
    prepareRequestInFlight.current = true;
    setWorkingPrepareBotId(bot.id);
    setPrepareConfirmationError('');
    try {
      const response = await axiosInstance.post(`/api/bots/${bot.id}/promote`, {
        reason: 'Owner prepared the bot for Practice from Your Bots',
        idempotency_key: crypto.randomUUID(),
      });
      setBotActionNotice({
        tone: 'success',
        message: response.data?.message || 'Market-data preparation was requested.',
      });
      setPrepareConfirmation(null);
      window.requestAnimationFrame(() => prepareTriggerRef.current?.focus());
      await botsQuery.refetch();
    } catch (error) {
      const message = customerErrorMessage(
        error.response?.data?.detail,
        'Unable to prepare this bot. Nothing was started.',
      );
      setPrepareConfirmationError(message);
      setBotActionNotice({ tone: 'error', message });
    } finally {
      prepareRequestInFlight.current = false;
      setWorkingPrepareBotId(null);
    }
  };

  const demoteBot = async (bot) => {
    if (!window.confirm(`Return "${bot.name}" to draft and clear the current practice symbol list?`)) {
      return;
    }
    try {
      await axiosInstance.post(`/api/bots/${bot.id}/demote`, {
        reason: 'Owner returned the bot to draft from Your Bots',
        idempotency_key: crypto.randomUUID(),
      });
      await botsQuery.refetch();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Unable to return this bot to draft.');
    }
  };

  const updateDryRun = async (bot, action) => {
    if (dryRunRequestsInFlight.current.has(bot.id)) return;
    dryRunRequestsInFlight.current.add(bot.id);
    setWorkingDryRunActions((current) => ({ ...current, [bot.id]: action }));
    if (action === 'start') {
      setStartErrors((current) => ({ ...current, [bot.id]: null }));
    } else {
      setBotRuntimeFeedback((current) => ({ ...current, [bot.id]: null }));
    }
    try {
      const response = await axiosInstance.post(
        `/api/bots/${bot.id}/runtime/dry-run/${action}`,
        {
          reason: `Owner requested Practice ${action} from Your Bots`,
          idempotency_key: crypto.randomUUID(),
        },
      );
      setDryRunStates((current) => {
        const priorGeneration = Number(current[bot.id]?.generation);
        const receivedGeneration = Number(response.data?.generation);
        if (Number.isFinite(priorGeneration) && Number.isFinite(receivedGeneration)
          && receivedGeneration < priorGeneration) return current;
        return { ...current, [bot.id]: response.data };
      });
      if (action === 'start') {
        if (createdBotId === bot.id) {
          setCreatedBotId(null);
          setFormSuccess('');
        }
        setStartFeedbackToReveal({ botId: bot.id });
        liveActivityQuery.refetch();
      }
      if (action === 'pause' || action === 'stop') {
        const expectedState = action === 'pause' ? 'pause' : 'stop';
        setBotRuntimeFeedback((current) => ({ ...current, [bot.id]: {
          action,
          tone: response.data?.desired_state === expectedState ? 'success' : 'pending',
          message: response.data?.desired_state === expectedState
            ? action === 'pause' ? 'Practice checks paused.' : 'Practice checks ended.'
            : 'Request accepted — checking the current Practice status.',
        } }));
        liveActivityQuery.refetch();
      }
      if (action === 'stop') await botsQuery.refetch();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.message || (action === 'start'
          ? 'Start request could not be confirmed. Please retry.'
          : `Unable to ${action === 'pause' ? 'pause' : 'end'} Practice checks. Please retry.`);
      if (action === 'start') {
        setStartErrors((current) => ({ ...current, [bot.id]: message }));
        setStartFeedbackToReveal({ botId: bot.id });
      } else {
        setBotRuntimeFeedback((current) => ({ ...current, [bot.id]: {
          action, tone: 'error', message,
        } }));
      }
    } finally {
      dryRunRequestsInFlight.current.delete(bot.id);
      setWorkingDryRunActions((current) => {
        const next = { ...current };
        delete next[bot.id];
        return next;
      });
    }
  };

  const updatePaperRun = async (bot, action) => {
    if (paperRequestsInFlight.current.has(bot.id)) return;
    paperRequestsInFlight.current.add(bot.id);
    setWorkingPaperActions((current) => ({ ...current, [bot.id]: action }));
    setBotPaperFeedback((current) => ({ ...current, [bot.id]: null }));
    try {
      const response = await axiosInstance.post(`/api/bots/${bot.id}/runtime/paper/${action}`);
      setPaperStates((current) => ({ ...current, [bot.id]: response.data }));
      const runStatus = response.data?.run?.run_status;
      setBotPaperFeedback((current) => ({ ...current, [bot.id]: {
        action,
        tone: action === 'start' ? runStatus === 'active' ? 'success' : 'pending' : runStatus === 'stopped' ? 'success' : 'pending',
        message: action === 'start'
          ? runStatus === 'active' ? 'Virtual portfolio active.' : 'Request accepted — checking portfolio status.'
          : runStatus === 'stopped' ? 'Virtual portfolio ended.' : 'End requested — waiting for virtual positions to close.',
      } }));
    } catch (error) {
      const detail = error.response?.data?.detail;
      setBotPaperFeedback((current) => ({ ...current, [bot.id]: {
        action,
        tone: 'error',
        message: typeof detail === 'string'
          ? detail
          : detail?.message || 'Unable to update the virtual portfolio. Please retry.',
      } }));
    } finally {
      paperRequestsInFlight.current.delete(bot.id);
      setWorkingPaperActions((current) => {
        const next = { ...current };
        delete next[bot.id];
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <NavigationBar />
      {companyContextStock && (
        <StockFocusCompanyDrawer
          stock={companyContextStock}
          stockFocus={botTemplate}
          lineage={screeningLineage}
          axiosInstance={axiosInstance}
          onClose={closeCompanyContext}
        />
      )}
      {prepareConfirmation && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center overflow-y-auto bg-slate-950/70 p-4 sm:items-center" role="presentation">
          <section
            id="prepare-bot-dialog"
            ref={prepareDialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="prepare-bot-dialog-title"
            aria-describedby="prepare-bot-dialog-description"
            className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl dark:bg-slate-900 sm:p-6"
          >
            <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400">Prepare bot</p>
            <h2 id="prepare-bot-dialog-title" className="mt-2 break-words text-2xl font-black">Prepare {prepareConfirmation.name}?</h2>
            <p id="prepare-bot-dialog-description" className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Pinngro checks that the market data needed by this bot is ready. Nothing will start automatically, and no Practice or broker order is created.
            </p>
            {prepareConfirmationError && (
              <p role="alert" className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">
                {prepareConfirmationError}
              </p>
            )}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closePrepareConfirmation}
                disabled={workingPrepareBotId === prepareConfirmation.id}
                className="min-h-11 rounded-xl border border-slate-300 px-5 py-3 font-bold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                ref={prepareConfirmButtonRef}
                type="button"
                onClick={promoteBot}
                disabled={workingPrepareBotId === prepareConfirmation.id}
                className="min-h-11 rounded-xl bg-emerald-600 px-5 py-3 font-black text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {workingPrepareBotId === prepareConfirmation.id ? 'Preparing…' : 'Prepare bot'}
              </button>
            </div>
          </section>
        </div>
      )}
      {revisionConfirmation && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center overflow-y-auto bg-slate-950/70 p-4 sm:items-center" role="presentation">
          <section
            ref={revisionDialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="strategy-revision-dialog-title"
            aria-describedby="strategy-revision-dialog-description"
            className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl dark:bg-slate-900 sm:p-6"
          >
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">New strategy version</p>
            <h2 id="strategy-revision-dialog-title" className="mt-2 break-words text-2xl font-black">Review {advancedStrategyName}</h2>
            <div id="strategy-revision-dialog-description" className="mt-4 space-y-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
              <p><strong className="text-slate-900 dark:text-white">Maximum holding period:</strong> {revisionConfirmation.summary.display}</p>
              <p><strong className="text-slate-900 dark:text-white">What happens:</strong> {revisionConfirmation.summary.whatHappens}</p>
              {revisionConfirmation.summary.enabled && (
                <>
                  <p>A position may close earlier if its stop loss, take profit, or technical exit triggers first.</p>
                  <p><strong className="text-slate-900 dark:text-white">Practice:</strong> Manual approval is still required before a close at the next eligible market open.</p>
                </>
              )}
              <p>The current version and its historical results stay unchanged. Confirming creates a new version for this draft bot.</p>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setRevisionConfirmation(null);
                  window.requestAnimationFrame(() => revisionTriggerRef.current?.focus());
                }}
                className="min-h-11 rounded-xl border border-slate-300 px-5 py-3 font-bold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Keep editing
              </button>
              <button
                ref={revisionConfirmButtonRef}
                type="button"
                onClick={confirmStrategyRevision}
                disabled={savingBot}
                className="min-h-11 rounded-xl bg-blue-600 px-5 py-3 font-black text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingBot ? 'Creating version…' : 'Create new version'}
              </button>
            </div>
          </section>
        </div>
      )}
      <main className="mx-auto max-w-7xl px-4 pb-16 pt-24 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-3xl bg-slate-950 text-white shadow-xl dark:border dark:border-slate-800">
          <div className={`relative grid sm:px-10 lg:px-14 ${firstBotJourney ? 'gap-5 px-6 py-6 lg:grid-cols-[1.4fr_0.8fr] lg:py-10' : 'gap-4 px-6 py-5 lg:py-6'}`}>
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="relative">
              <p className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">
                <FaRobot /> Your strategy team
              </p>
              <h1 className={`max-w-3xl font-black tracking-tight ${firstBotJourney ? 'text-3xl sm:text-5xl' : 'text-3xl sm:text-4xl'}`}>
                {firstBotJourney ? 'Create your first Practice bot.' : 'Your bots at work.'}
              </h1>
              <p className="mt-3 max-w-2xl text-base leading-7 text-slate-300">
                {firstBotJourney
                  ? 'Choose its stocks, trading strategy, and maximum allocation. Nothing starts automatically.'
                  : `${(botsQuery.data || []).length} Practice ${(botsQuery.data || []).length === 1 ? 'bot' : 'bots'} · order-free. See who is watching, resting, or needs attention.`}
              </p>
              {welcomedFromAlpaca && (
                <p role="status" className="mt-5 rounded-xl border border-emerald-300/30 bg-emerald-400/10 p-4 font-bold text-emerald-200">Market data connected. Your next step is to create your first bot.</p>
              )}
            </div>
            {firstBotJourney && <div className="relative hidden rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur lg:block">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">Practice bots</span>
                <span className="rounded-full bg-emerald-400/20 px-3 py-1 text-xs font-bold text-emerald-300">Order-free</span>
              </div>
              <p className="mt-3 text-3xl font-black">{(botsQuery.data || []).length}</p>
              <p className="mt-1 text-sm text-slate-400">specialists in your strategy team</p>
            <p className="mt-3 text-sm leading-6 text-slate-300">Each bot is isolated and cannot submit real or Alpaca Paper orders.</p>
            </div>}
          </div>
        </section>

        <details className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <summary className="cursor-pointer font-black">How Pinngro Practice bots work</summary>
        <section className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <FaShieldAlt className="text-xl text-emerald-500" />
            <h2 className="mt-4 font-bold">Your rules come first</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
              Each bot uses your saved strategy, assigned symbols, and explicit maximum allocation.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <FaChartLine className="text-xl text-blue-500" />
            <h2 className="mt-4 font-bold">Practice before trusting</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
              Start with Pinngro Practice before considering any future execution mode.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/60 dark:bg-amber-950/30">
            <FaExclamationTriangle className="text-xl text-amber-500" />
            <h2 className="mt-4 font-bold">Pinngro Practice only</h2>
            <p className="mt-2 text-sm leading-6 text-amber-800 dark:text-amber-300">
              These bots check markets and report. They cannot submit orders. Historical development-era bots remain separate and do not change modern bots.
            </p>
          </div>
        </section>
        </details>

        <section className={`${firstBotJourney ? 'mt-5' : 'mt-4'} flex flex-col gap-8`}>
          <div ref={botListRegionRef} className={firstBotJourney ? 'hidden' : 'order-1'}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">
                  Your bots
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-tight">Your team</h2>
                <p className="mt-2 text-slate-600 dark:text-slate-400">
                  See who is working, who is resting, and what each specialist has been assigned.
                </p>
              </div>
              {!firstBotJourney && !showCreator && !editingBotId && (
                <button
                  type="button"
                  onClick={openBotCreator}
                  className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
                >
                  Create another bot
                </button>
              )}
            </div>

            {botActionNotice && (
              <p
                role={botActionNotice.tone === 'error' ? 'alert' : 'status'}
                className={`mt-5 rounded-xl p-3 text-sm font-semibold ${botActionNotice.tone === 'error'
                  ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                  : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'}`}
              >
                {botActionNotice.message}
              </p>
            )}

            {botsQuery.isLoading ? (
              <div className="py-12"><LoadingSpinner /></div>
            ) : botsQuery.isError ? (
              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                Saved bots are unavailable until the draft-bot database migration is applied.
              </div>
            ) : botsQuery.data.length === 0 ? (
              <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-900">
                <FaRobot className="mx-auto text-3xl text-slate-400" />
                <h3 className="mt-4 text-lg font-bold">Create your first bot</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Give it a name, choose its stocks and strategy, then create it. Nothing starts automatically.
                </p>
                <a href="#hire" onClick={openBotCreator} className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-black text-white hover:bg-blue-700">
                  Create your first bot <FaArrowRight />
                </a>
              </div>
            ) : (
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {botsQuery.data.map((bot) => {
                  const lifecycle = bot.lifecycle || {};
                  const modernStrategy = canPrepareBot(bot);
                  const lifecycleSteps = [
                    { label: 'Draft', passed: true },
                    { label: 'Pinngro Practice', passed: lifecycle.dry_run_complete },
                    { label: 'Practice portfolio', passed: lifecycle.paper_trading_eligible },
                    { label: 'Live unavailable', passed: false, locked: true },
                  ];
                  const paperState = paperStates[bot.id];
                  const paperRun = paperState?.run;
                  const paperActive = paperRun?.run_status === 'active';
                  const runtime = dryRunStates[bot.id];
                  const readiness = presentBotReadiness(bot, runtime);
                  const workingDryRunAction = workingDryRunActions[bot.id];
                  const workingPaperAction = workingPaperActions[bot.id];
                  const recordedRuntimeFeedback = botRuntimeFeedback[bot.id];
                  const runtimeFeedbackConfirmed = runtime?.desired_state === recordedRuntimeFeedback?.action;
                  const runtimeFeedback = recordedRuntimeFeedback?.tone === 'pending' && runtimeFeedbackConfirmed
                    ? { ...recordedRuntimeFeedback, tone: 'success', message: recordedRuntimeFeedback.action === 'pause' ? 'Practice checks paused.' : 'Practice checks ended.' }
                    : recordedRuntimeFeedback?.tone === 'success' && !runtimeFeedbackConfirmed
                      ? null : recordedRuntimeFeedback;
                  const recordedPaperFeedback = botPaperFeedback[bot.id];
                  const paperFeedbackConfirmed = recordedPaperFeedback?.action === 'start'
                    ? paperRun?.run_status === 'active'
                    : paperRun?.run_status === 'stopped';
                  const paperFeedback = recordedPaperFeedback?.tone === 'pending' && paperFeedbackConfirmed
                    ? { ...recordedPaperFeedback, tone: 'success', message: recordedPaperFeedback.action === 'start' ? 'Virtual portfolio active.' : 'Virtual portfolio ended.' }
                    : recordedPaperFeedback?.tone === 'success' && !paperFeedbackConfirmed
                      ? null : recordedPaperFeedback;
                  const preparationOpen = ['PENDING', 'RUNNING'].includes(
                    bot.market_data_preparation?.status,
                  );
                  const plainState = beginnerBotState(bot, runtime, paperState);
                  const needsAlpaca = readiness.reconnect || (oauthMissing && bot.status === 'ready');
                  return (
                  <article id={`bot-${bot.id}`} key={bot.id} tabIndex={-1} className={`min-w-0 scroll-mt-24 rounded-2xl border bg-white p-5 focus:outline-none target:border-blue-500 target:ring-4 target:ring-blue-100 dark:bg-slate-900 dark:target:ring-blue-950 ${createdBotId === bot.id ? 'border-emerald-500 ring-4 ring-emerald-100 dark:ring-emerald-950' : 'border-slate-200 dark:border-slate-800'}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/bots#bot-${bot.id}`}
                          aria-label={`Open ${bot.name}`}
                          className="group block max-w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <BotIdentity bot={bot} />
                          <span className="mt-2 inline-flex items-center gap-1 text-xs font-black text-blue-700 group-hover:underline dark:text-blue-300">
                            Open bot <FaArrowRight aria-hidden="true" />
                          </span>
                        </Link>
                      </div>
                      <div className="flex gap-1">
                        {bot.status === 'draft' && modernStrategy && (
                          <>
                            <button
                              type="button"
                              onClick={() => editBot(bot)}
                              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-blue-700 dark:hover:bg-slate-800"
                              aria-label={`Edit ${bot.name}`}
                            >
                              <FaPencilAlt />
                            </button>
                            <button
                              type="button"
                              onClick={() => archiveBot(bot)}
                              className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40"
                              aria-label={`Archive ${bot.name}`}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <BotLiveActivity bot={liveActivityQuery.data?.bots?.find((item) => item.bot_id === bot.id)} identityBot={bot} query={liveActivityQuery} compact />
                        <p className="mt-3 text-sm text-slate-500"><strong className="text-slate-700 dark:text-slate-200">Watches:</strong> {(bot.symbols || []).join(', ') || 'No stocks assigned'}</p>
                        <p className="mt-1 text-sm text-slate-500"><strong className="text-slate-700 dark:text-slate-200">{bot.stock_selection_mode === 'ALL_STOCKS' ? 'Find stocks:' : 'Stock Focus:'}</strong> {bot.stock_selection_mode === 'ALL_STOCKS' ? 'All Stocks — chosen directly by you' : bot.template_name}</p>
                        <p className="mt-1 text-sm text-slate-500">Trading strategy: {strategyDisplayName(bot.trading_strategy_family, bot.trading_strategy_name)}</p>
                        {bot.experimental_strategy && <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs leading-5 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"><strong>{bot.trading_strategy_family === 'trend-pullback-v2-experimental' ? 'Practice only' : bot.experimental_strategy.classification} · {strategyDisplayName(bot.trading_strategy_family, bot.experimental_strategy.label)}</strong><br />{bot.experimental_strategy.summary}</p>}
                    <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-sm dark:border-slate-800">
                      <span className="text-slate-500">Maximum allocation</span>
                      <span className="font-black">{formatPercent(bot.risk_budget_pct)}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="text-slate-500">Assigned symbols</span>
                      <span className="font-black">{(bot.symbols || []).length}</span>
                    </div>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                      Pinngro Practice · {plainState}
                    </p>
                    {createdBotId === bot.id && (
                      <div className="mt-4 space-y-3">
                        <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
                          <p className="font-black">Your bot has been created.</p>
                          <p className="mt-1">Its next available action is shown below. Nothing started automatically.</p>
                        </div>
                        {(premiumBacktesting || nonPremiumBacktesting) && (
                          <section aria-labelledby={`historical-simulation-${bot.id}`} className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100" data-testid="post-create-backtest-handoff">
                            <p className="text-xs font-black uppercase tracking-wide text-blue-700 dark:text-blue-300">Historical simulation</p>
                            <h3 id={`historical-simulation-${bot.id}`} className="mt-1 font-black">Explore historical behaviour</h3>
                            <p className="mt-1 text-sm leading-6">See how this strategy behaved historically for one stock.</p>
                            {premiumBacktesting ? (
                              <Link to={backtestHandoffPath(bot.id)} className="mt-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-blue-300 bg-white px-4 py-2.5 text-sm font-bold text-blue-700 hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-blue-800 dark:bg-slate-900 dark:text-blue-300 dark:hover:bg-blue-950">
                                Explore historical behaviour <FaArrowRight aria-hidden="true" />
                              </Link>
                            ) : (
                              <Link to="/subscribe" className="mt-3 inline-flex min-h-11 items-center justify-center rounded-xl border border-blue-300 bg-white px-4 py-2.5 text-sm font-bold text-blue-700 hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-blue-800 dark:bg-slate-900 dark:text-blue-300 dark:hover:bg-blue-950">
                                See plans with Backtesting
                              </Link>
                            )}
                          </section>
                        )}
                      </div>
                    )}
                    <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                      <summary className="cursor-pointer text-xs font-black uppercase tracking-wide text-slate-600 dark:text-slate-300">More bot details</summary>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-black uppercase tracking-wide text-slate-500">Safety path</span>
                        <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
                          {lifecycle.stage === 'paper_ready' ? 'Practice reviewed' : lifecycle.stage === 'dry_run' ? 'Pinngro Practice' : 'Draft'}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <span className="text-xs font-black uppercase tracking-wide text-slate-500">Practice approval</span>
                        <span className="text-right text-xs font-bold text-violet-700 dark:text-violet-300">
                          {bot.practice_approval_mode === 'MANUAL_APPROVAL_REQUIRED'
                            ? 'Manual approval required'
                            : 'Adopted when the owner next starts Practice'}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-4 gap-1" aria-label="Bot lifecycle">
                        {lifecycleSteps.map((step, index) => (
                          <div key={step.label} className="text-center">
                            <div className={`mx-auto flex h-7 w-7 items-center justify-center rounded-full text-xs ${step.passed ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500 dark:bg-slate-700'}`}>
                              {step.passed ? <FaCheckCircle /> : step.locked ? <FaLock /> : index + 1}
                            </div>
                            <p className="mt-1 text-[10px] font-bold leading-3 text-slate-500">{step.label}</p>
                          </div>
                        ))}
                      </div>
                      {lifecycle.blockers?.length > 0 && (
                        <p className="mt-3 border-t border-slate-200 pt-3 text-xs leading-5 text-slate-600 dark:border-slate-700 dark:text-slate-300">
                          <span className="font-bold">Next:</span> {lifecycle.blockers[0]}
                        </p>
                      )}
                    {lifecycle.paper_trading_eligible && (
                        <p className="mt-3 rounded-lg bg-emerald-50 p-2 text-xs font-semibold leading-5 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                          Practice checks complete. Start the Pinngro Practice portfolio to review virtual fills and risk behavior.
                        </p>
                      )}
                    </details>
                    {paperRun && (
                      <div className={`mt-4 rounded-xl border p-3 text-xs ${paperRun.run_status === 'halted' ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30' : 'border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/30'}`}>
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-2 font-bold"><FaWallet /> Practice portfolio</span>
                          <span className="font-black uppercase">{paperRun.run_status}</span>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                          <PaperMetric label="Virtual equity" value={money(paperRun.equity)} />
                          <PaperMetric label="Realized P&L" value={money(paperRun.realized_pnl)} />
                          <PaperMetric label="Drawdown" value={`${Number(paperRun.drawdown_pct || 0).toFixed(2)}%`} />
                          <PaperMetric label="Positions" value={paperState.positions?.length || 0} />
                        </div>
                        {activePendingAction(paperState) && (
                          <p className="mt-3 rounded-lg bg-white/70 p-2 font-semibold text-violet-900 dark:bg-slate-900/60 dark:text-violet-200">
                            {activePendingAction(paperState).proposal_status === 'PENDING'
                              ? 'A strategy suggestion is waiting in Today. No virtual transaction occurs unless you approve it before the deadline.'
                              : 'You approved this virtual action for the next eligible session open. Approval does not guarantee a fill.'}
                          </p>
                        )}
                        {paperRun.run_status === 'halted' && <p className="mt-3 font-semibold text-red-800 dark:text-red-300">Halted by the configured drawdown limit.</p>}
                      </div>
                    )}
                    {bot.status === 'draft' && modernStrategy && (
                      <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs dark:border-blue-900 dark:bg-blue-950/30">
                        <p className="font-bold text-blue-900 dark:text-blue-200">Prepare bot</p>
                        <p className="mt-2 leading-5 text-blue-800 dark:text-blue-300">Pinngro checks that the market data needed by this bot is ready.</p>
                        <p className="mt-1 leading-5 text-blue-800 dark:text-blue-300">{readiness.detail}</p>
                        {readiness.failureSymbols?.length > 0 && (
                          <p className="mt-2 font-semibold text-amber-800 dark:text-amber-300">
                            Affected stocks: {readiness.failureSymbols.join(', ')}
                          </p>
                        )}
                        {readiness.reconnect && (
                          <Link to="/account-settings" className="mt-3 inline-flex rounded-lg bg-amber-600 px-3 py-2 font-bold text-white hover:bg-amber-700">Connect / reconnect Alpaca</Link>
                        )}
                      </div>
                    )}
                    {bot.status === 'ready' && (
                      <div className="mt-4 rounded-xl border border-cyan-200 bg-cyan-50 p-3 text-xs dark:border-cyan-900 dark:bg-cyan-950/30">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-bold text-cyan-900 dark:text-cyan-200">Pinngro Practice status</span>
                          <span className="rounded-full bg-white px-2 py-1 font-black uppercase text-cyan-700 dark:bg-slate-900 dark:text-cyan-300">
                            {dryRunStates[bot.id]?.desired_state || 'not started'}
                          </span>
                        </div>
                        <p className="mt-2 leading-5 text-cyan-800 dark:text-cyan-300">
                          {readiness.detail} A Strategy Check may create a BUY or SELL suggestion in Today; a signal alone cannot create a virtual transaction.
                        </p>
                        {readiness.nextExpectedSessionDate && !readiness.lastSuccessfulCheck && (
                          <p className="mt-2 text-cyan-700 dark:text-cyan-400">Next supported session: {readiness.nextExpectedSessionDate}</p>
                        )}
                        {needsAlpaca && (
                          <Link to="/account-settings" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-2 font-bold text-white hover:bg-amber-700">Connect / reconnect Alpaca</Link>
                        )}
                        {dryRunStates[bot.id]?.error && (
                          <p className="mt-2 text-red-700 dark:text-red-300">{dryRunStates[bot.id].error}</p>
                        )}
                      </div>
                    )}
                    {bot.status === 'ready' && (
                      <details className="mt-3 rounded-xl border border-slate-200 p-3 text-xs leading-5 text-slate-600 dark:border-slate-700 dark:text-slate-300">
                        <summary className="cursor-pointer font-bold">What do these Practice controls do?</summary>
                        <p className="mt-2"><strong>Practice checks</strong> run Market Checks and Strategy Checks. Ending them prevents further checks and new actions.</p>
                        <p className="mt-2"><strong>Virtual portfolio</strong> records virtual positions, fills, and results after the existing eligibility step. Ending it uses the existing safe portfolio lifecycle; it does not place a broker order.</p>
                      </details>
                    )}
                    {bot.status === 'draft' && modernStrategy ? (
                      <button
                        type="button"
                        onClick={(event) => openPrepareConfirmation(bot, event.currentTarget)}
                        disabled={!bot.symbols?.length || preparationOpen || workingPrepareBotId === bot.id}
                        aria-haspopup="dialog"
                        aria-controls="prepare-bot-dialog"
                        className="mt-4 w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {preparationOpen
                          ? bot.market_data_preparation?.status === 'RUNNING'
                            ? 'Preparing…'
                            : 'Preparation queued…'
                          : readiness.retryAvailable
                            ? 'Retry preparation'
                            : 'Prepare bot'}
                      </button>
                    ) : bot.status === 'draft' ? (
                      <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                        This historical development draft is read-only and cannot be prepared or started.
                      </p>
                    ) : bot.status === 'ready' ? (
                      <div className="mt-4 space-y-2">
                        {workingDryRunAction === 'start' || (runtime?.desired_state !== 'run'
                          && (!readiness.needsAttention || startErrors[bot.id])) ? (
                          <button
                            type="button"
                            onClick={() => updateDryRun(bot, 'start')}
                            disabled={Boolean(workingDryRunAction) || needsAlpaca}
                            aria-describedby={needsAlpaca ? `bot-${bot.id}-start-blocker` : undefined}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-600 px-4 py-3 text-sm font-black text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {workingDryRunAction === 'start' && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white motion-reduce:animate-none" />}
                            {workingDryRunAction === 'start'
                              ? 'Starting…'
                              : runtime?.desired_state === 'pause' ? 'Resume watching' : 'Start watching'}
                          </button>
                        ) : readiness.needsAttention ? (
                          <Link to={`/bots/${bot.id}/report#market-check-status`} className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-3 text-sm font-black text-white hover:bg-amber-700">
                            Review what needs attention <FaArrowRight />
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => updateDryRun(bot, 'pause')}
                            disabled={Boolean(workingDryRunAction)}
                            className="w-full rounded-xl border border-amber-400 px-4 py-3 text-sm font-bold text-amber-800 hover:bg-amber-50 disabled:opacity-50 dark:text-amber-300 dark:hover:bg-amber-950/30"
                          >
                            {workingDryRunAction === 'pause' ? 'Pausing…' : readiness.label === 'WATCHING' ? 'Pause watching' : 'Pause Practice checks'}
                          </button>
                        )}
                        {workingDryRunAction === 'pause' && <InlineBotActionFeedback feedback={{ tone: 'pending', message: 'Sending pause request…' }} id={`bot-${bot.id}-runtime-feedback`} />}
                        {!workingDryRunAction && runtimeFeedback?.action === 'pause' && <InlineBotActionFeedback feedback={runtimeFeedback} id={`bot-${bot.id}-runtime-feedback`} />}
                        {workingDryRunAction === 'start' && (
                          <p role="status" className="rounded-xl bg-cyan-50 p-3 text-sm font-semibold text-cyan-900 dark:bg-cyan-950/40 dark:text-cyan-200">
                            Sending start request…
                          </p>
                        )}
                        {!workingDryRunAction && runtime?.desired_state === 'run' && readiness.label !== 'WATCHING' && (
                          <p id={`bot-${bot.id}-start-feedback`} role="status" className="rounded-xl bg-cyan-50 p-3 text-sm font-semibold text-cyan-900 dark:bg-cyan-950/40 dark:text-cyan-200">
                            Watching requested — waiting for readiness confirmation
                          </p>
                        )}
                        {!workingDryRunAction && startErrors[bot.id] && runtime?.desired_state !== 'run' && (
                          <p id={`bot-${bot.id}-start-feedback`} role="alert" className="rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-800 dark:bg-red-950/40 dark:text-red-200">
                            {startErrors[bot.id]}
                          </p>
                        )}
                        {needsAlpaca && !readiness.needsAttention && (
                          <p id={`bot-${bot.id}-start-blocker`} className="text-sm text-amber-800 dark:text-amber-300">
                            Connect Alpaca for market data before starting.
                          </p>
                        )}
                        {!readiness.needsAttention && (
                          <Link to={`/bots/${bot.id}/report`} className="flex w-full items-center justify-center gap-2 rounded-xl border border-blue-300 px-4 py-2.5 text-sm font-bold text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950/30">
                            View bot report <FaArrowRight />
                          </Link>
                        )}
                        <details className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                          <summary className="cursor-pointer text-sm font-bold">More bot controls</summary>
                          <div className="mt-3 space-y-2">
                            {lifecycle.paper_trading_eligible && !paperActive && paperRun?.run_status !== 'halted' && (
                              <button type="button" onClick={() => updatePaperRun(bot, 'start')} disabled={Boolean(workingPaperAction)} className="flex w-full items-center justify-center gap-2 rounded-xl border border-violet-300 px-4 py-2.5 text-sm font-bold text-violet-700 hover:bg-violet-50 disabled:opacity-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/30">
                                <FaPlay /> {workingPaperAction === 'start' ? 'Starting…' : 'Start virtual portfolio'}
                              </button>
                            )}
                            {paperActive && (
                              <button type="button" onClick={() => updatePaperRun(bot, 'stop')} disabled={Boolean(workingPaperAction)} className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-300 px-4 py-2.5 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30">
                                <FaStop /> {workingPaperAction === 'stop' ? 'Ending…' : 'End virtual portfolio'}
                              </button>
                            )}
                            {workingPaperAction && <InlineBotActionFeedback feedback={{ tone: 'pending', message: `${workingPaperAction === 'start' ? 'Starting' : 'Ending'} virtual portfolio…` }} id={`bot-${bot.id}-paper-feedback`} />}
                            {!workingPaperAction && paperFeedback && <InlineBotActionFeedback feedback={paperFeedback} id={`bot-${bot.id}-paper-feedback`} />}
                            <button
                              type="button"
                              onClick={() => updateDryRun(bot, 'stop')}
                              disabled={Boolean(workingDryRunAction) || !dryRunStates[bot.id]}
                              className="w-full rounded-xl border border-red-300 px-3 py-2.5 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-40 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30"
                            >
                              {workingDryRunAction === 'stop' ? 'Ending…' : 'End Practice checks'}
                            </button>
                            {workingDryRunAction === 'stop' && <InlineBotActionFeedback feedback={{ tone: 'pending', message: 'Ending Practice checks…' }} id={`bot-${bot.id}-runtime-stop-feedback`} />}
                            {!workingDryRunAction && runtimeFeedback?.action === 'stop' && <InlineBotActionFeedback feedback={runtimeFeedback} id={`bot-${bot.id}-runtime-stop-feedback`} />}
                            <button
                              type="button"
                              onClick={() => demoteBot(bot)}
                              disabled={dryRunStates[bot.id]?.desired_state === 'run'}
                              className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:hover:bg-slate-800"
                            >
                              Return to draft
                            </button>
                          </div>
                        </details>
                      </div>
                    ) : null}
                  </article>
                  );
                })}
              </div>
            )}
          </div>

          <form ref={creationSectionRef} id="hire" aria-labelledby="bot-creation-heading" onSubmit={saveBot} className={`order-2 h-fit scroll-mt-16 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:scroll-mt-24 sm:p-8 ${showCreator || editingBotId || firstBotJourney ? '' : 'hidden'}`}>
            <h2 id="bot-creation-heading" className="text-xl font-black">{editingBotId ? 'Edit bot' : creationMode === 'simple' ? 'Create a Pinngro Practice bot' : 'Create an advanced bot'}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              {creationMode === 'simple'
                ? 'Follow the short sequence below. You will review everything before choosing Create bot.'
                : 'Advanced lets you build your own entry and exit rules. Your changes belong to your bot and never alter a Pinngro strategy for anyone else.'}
            </p>
            {!editingBotId && creationMode === 'simple' && percentageSetupHandoff && selectedCustomStrategy && botSymbols && (
              <p role="status" className="mt-3 rounded-xl bg-blue-50 p-3 text-sm leading-5 text-blue-900 dark:bg-blue-950/30 dark:text-blue-200">Your saved strategy, {selectedCustomStrategy.strategy_name}, and {botSymbols} are preselected. Review the bot name, stock and maximum allocation before creating this Practice bot.</p>
            )}
            {!editingBotId && creationMode === 'simple' && stockSetupHandoff && botSymbols && (
              <p role="status" className="mt-3 rounded-xl bg-blue-50 p-3 text-sm leading-5 text-blue-900 dark:bg-blue-950/30 dark:text-blue-200">Stock Explorer added a stock to this bot setup. Currently selected: {botSymbols}. Choose a trading strategy, then review the stocks and allocation before creating your Practice bot.</p>
            )}
            {!editingBotId && creationMode === 'simple' && simpleStep === 1 && !percentageSetupHandoff && !stockSetupHandoff && (
              <p className="mt-3 rounded-xl bg-blue-50 p-3 text-xs leading-5 text-blue-900 dark:bg-blue-950/30 dark:text-blue-200"><strong>Beginner starting example:</strong> Blue-Chip Stability with Trend Retracement is preselected as a learning example, not investment advice. Change either choice before creating your bot.</p>
            )}
            {!editingBotId && (
              <div className="mt-5 grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1 dark:bg-slate-800" aria-label="Bot setup mode">
                {[
                  ['simple', 'Simple'],
                  ['advanced', 'Advanced'],
                ].map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={creationMode === mode}
                    onClick={() => {
                      setCreationMode(mode);
                      if (mode === 'simple') {
                        restoreSimpleForm();
                      } else {
                        setBotName('');
                        setBotTemplate('');
                        setRiskBudget('10');
                        setBotSymbols('');
                        setScreeningLineage(null);
                        setSelectionMethod(null);
                        setNewsSentimentEnabled(false);
                        setAdvancedFactoryStrategy('');
                        setAdvancedScreeningDefinitionId('');
                        const custom = customStrategyTemplateQuery.data;
                        setAdvancedStrategyName(custom?.strategy_name || 'My custom strategy');
                        setAdvancedStrategyDefinition(custom?.strategy_configuration
                          ? JSON.stringify(custom.strategy_configuration, null, 2)
                          : '');
                      }
                      setFormError('');
                      setFormSuccess('');
                    }}
                    className={`rounded-lg px-3 py-2 text-sm font-bold ${creationMode === mode ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-950 dark:text-blue-300' : 'text-slate-600 dark:text-slate-300'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {!editingBotId && creationMode === 'simple' && (
              <ol className="mt-5 grid grid-cols-2 gap-1 text-center text-[10px] font-bold sm:grid-cols-5 sm:text-xs" aria-label="Bot setup progress">
                {['Name + find stocks', 'Strategy', 'Stocks', 'Allocation', 'Review & create'].map((label, index) => (
                  <li key={label}>
                    <button
                      type="button"
                      disabled={!canOpenSimpleStep(index)}
                      aria-current={simpleStep === index + 1 ? 'step' : undefined}
                      onClick={() => setSimpleStep(index + 1)}
                      data-step-state={simpleStepState(index)}
                      className={`w-full rounded-lg px-1 py-2 ${simpleStepState(index) === 'completed' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' : simpleStepState(index) === 'current' ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-400 dark:bg-blue-950/50 dark:text-blue-300' : 'bg-slate-100 text-slate-500 disabled:cursor-default dark:bg-slate-800'}`}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ol>
            )}
            {!editingBotId && creationMode === 'simple' && !formSuccess && (
              <p className="mt-2 text-xs leading-5 text-slate-500">Your setup is saved in this browser tab as you work. No bot is created until you confirm the final step.</p>
            )}
            <div className={!editingBotId && creationMode === 'simple' && simpleStep !== 1 ? 'hidden' : ''}>
            <h3 className="mt-6 text-lg font-black">1. Name your bot</h3>
            <label className="mt-2 block text-sm font-bold" htmlFor="bot-name">Bot name</label>
            <p id="bot-name-guidance" className="mt-1 text-xs leading-5 text-slate-500">Give it any name that helps you recognise it.</p>
            <input
              ref={botNameInputRef}
              id="bot-name"
              aria-describedby="bot-name-guidance"
              value={botName}
              onChange={(event) => {
                setBotName(event.target.value);
                setBotNameSource('user');
              }}
              minLength={3}
              maxLength={100}
              required
              placeholder="e.g. Steady Blue Chip"
              className="mt-2 w-full rounded-xl border border-slate-300 bg-transparent px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700"
            />
            {creationMode === 'simple' ? <>
              <h3 className="mt-6 text-lg font-black">2. Choose how to find stocks</h3>
              <fieldset className="mt-3 grid gap-3 sm:grid-cols-2" aria-describedby="stock-discovery-guidance">
                <legend className="sr-only">Choose how to find stocks</legend>
                <label className={`cursor-pointer rounded-xl border p-4 ${stockDiscoveryMode === 'ALL_STOCKS' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 dark:bg-blue-950/30' : 'border-slate-300 dark:border-slate-700'}`}>
                  <input type="radio" name="stock-discovery-mode" value="ALL_STOCKS" checked={stockDiscoveryMode === 'ALL_STOCKS'} onChange={() => changeStockDiscoveryMode('ALL_STOCKS')} className="mr-2" />
                  <strong>All Stocks</strong>
                  <span className="mt-1 block text-xs leading-5 text-slate-600 dark:text-slate-300">Browse and search all stocks supported by Pinngro. You choose exactly which stocks this bot watches.</span>
                </label>
                <label className={`cursor-pointer rounded-xl border p-4 ${stockDiscoveryMode === 'STOCK_FOCUS' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 dark:bg-blue-950/30' : 'border-slate-300 dark:border-slate-700'}`}>
                  <input type="radio" name="stock-discovery-mode" value="STOCK_FOCUS" checked={stockDiscoveryMode === 'STOCK_FOCUS'} onChange={() => changeStockDiscoveryMode('STOCK_FOCUS')} className="mr-2" />
                  <strong>Stock Focus</strong>
                  <span className="mt-1 block text-xs leading-5 text-slate-600 dark:text-slate-300">Stock Focus narrows the market to companies matching these criteria. You choose which stocks your bot actually watches.</span>
                </label>
              </fieldset>
              <p id="stock-discovery-guidance" className="sr-only">All Stocks uses the supported stock universe. Stock Focus filters that universe with a profile.</p>
              {stockDiscoveryMode === 'STOCK_FOCUS' && <>
                <label className="mt-4 block text-sm font-bold" htmlFor="bot-template">Choose a Stock Focus</label>
                <select id="bot-template" value={botTemplate} onChange={(event) => {
                  setBotTemplate(event.target.value);
                  setScreeningLineage(null);
                  setSelectionMethod(null);
                  setPickResult(null);
                  setPickUndoSnapshot(null);
                }} required className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900">
                  <option value="">Choose what kinds of stocks to look for</option>
                  {activeTemplateEntries.map(([name]) => <option key={name} value={name}>{name}</option>)}
                </select>
              </>}
            </> : <>
              <label className="mt-5 block text-sm font-bold" htmlFor="advanced-custom-screening">1. Custom Screening</label>
              <p className="mt-2 text-xs leading-5 text-slate-500">Custom Screening chooses candidate stocks. Your Trading Strategy below independently decides when to buy or sell.</p>
              <select id="advanced-custom-screening" value={advancedScreeningDefinitionId} onChange={(event) => setAdvancedScreeningDefinitionId(event.target.value)} required className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900">
                <option value="">Choose your saved screening rules</option>
                {customScreeningDefinitions.map((definition) => <option key={definition.id} value={definition.id}>{definition.name}</option>)}
              </select>
              {selectedCustomScreening ? <div className="mt-3 rounded-xl border border-cyan-200 bg-cyan-50 p-3 text-xs dark:border-cyan-900 dark:bg-cyan-950/30"><p><strong>Saved screening rules:</strong> {selectedCustomScreening.name}</p><p className="mt-1">Once saved, this version won&apos;t change.</p><details className="mt-2"><summary className="cursor-pointer font-bold">Technical details</summary><p className="mt-1 break-all font-mono">Definition hash: {selectedCustomScreening.configuration_hash}</p></details></div> : <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">No screening rules selected. <Link to="/settings" className="font-bold underline">Create them in Strategy Workshop</Link>.</p>}
            </>}
            {creationMode === 'simple' && stockDiscoveryMode === 'STOCK_FOCUS' && botTemplate && PRESET_GUIDANCE[botTemplate] && (
              <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-slate-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-slate-200">
                <p className="mt-2"><strong>Risk style:</strong> {PRESET_GUIDANCE[botTemplate].risk}</p>
                <p className="mt-2"><strong>Suitable for:</strong> {PRESET_GUIDANCE[botTemplate].suitableFor}</p>
                <p className="mt-3 border-t border-blue-200 pt-3 dark:border-blue-800">
                  <strong>This helps you find matching stocks. You still choose every stock your bot watches.</strong>
                </p>
                <details className="mt-3 rounded-lg bg-white/70 p-3 dark:bg-slate-900/60">
                  <summary className="cursor-pointer font-black">Learn more</summary>
                  <p className="mt-2"><strong>What it watches:</strong> {PRESET_GUIDANCE[botTemplate].looksFor}</p>
                  <p className="mt-2"><strong>What makes it different:</strong> {PRESET_GUIDANCE[botTemplate].difference}</p>
                  <p className="mt-2"><strong>What it does not guarantee:</strong> {PRESET_GUIDANCE[botTemplate].notGuarantee}</p>
                </details>
              </div>
            )}
            {!editingBotId && creationMode === 'simple' && (
              <button type="button" disabled={!simpleStepComplete[0]} onClick={() => setSimpleStep(2)} className="mt-5 w-full rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">Next: Choose a strategy</button>
            )}
            </div>
            {creationMode === 'simple' && (
              <div className={`mt-5 ${!editingBotId && simpleStep !== 2 ? 'hidden' : ''}`}>
                {!editingBotId && <SetupSoFar />}
                {comparisonHandoff?.slug === botTradingStrategy && comparisonHandoff.kind === 'strategy' && <p ref={comparisonHandoffRef} tabIndex="-1" role="status" className="mb-4 rounded-xl bg-blue-50 p-3 text-sm font-semibold leading-6 text-blue-950 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:bg-blue-950/30 dark:text-blue-100">{comparisonHandoff.name} is selected. {botSymbols.trim() ? `Your chosen stocks are still here: ${botSymbols}.` : 'Choose your stocks next.'} Review the rules, then continue to stocks and allocation.</p>}
                <label className="block text-sm font-bold" htmlFor="bot-trading-strategy">
                  3. Choose a strategy
                </label>
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  How should your bot decide when to enter, hold, and exit? Choose a researched Pinngro strategy or one of your reusable strategies.
                </p>
                <p className="mt-4 text-xs font-black uppercase tracking-wide text-slate-500">Pinngro strategies</p>
                <div id="bot-trading-strategy" className="mt-2 grid gap-3 sm:grid-cols-2" role="group" aria-label="Pinngro strategies">
                  {factoryStrategies.map((strategy) => {
                    const selected = botTradingStrategy === strategy.slug;
                    const presentation = strategy.presentation || {};
                    const education = FACTORY_STRATEGY_EDUCATION[strategy.slug] || {};
                    return (
                      <button
                        key={strategy.slug}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => { setBotTradingStrategy(strategy.slug); setComparisonHandoff(null); setShortStrategyAcknowledged(false); }}
                        className={`rounded-2xl border p-4 text-left transition ${selected
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20 dark:bg-blue-950/30'
                          : 'border-slate-200 hover:border-blue-300 dark:border-slate-700'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span className="font-black">{strategyDisplayName(strategy.slug, strategy.name)}</span>
                          {(education.label || presentation.label || strategy.practice_only) && (
                            <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-black text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                              {education.label || presentation.label || 'PRACTICE ONLY'}
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
                          {education.summary || presentation.summary}
                        </p>
                        <p className="mt-3 text-xs leading-5 text-slate-600 dark:text-slate-300"><strong>Holding style:</strong> {education.holding || 'Varies with market conditions'}</p>
                        <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300"><strong>Relative risk:</strong> {education.relativeRisk || 'Review details'}</p>
                        <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300"><strong>Main risk:</strong> {education.risk || presentation.main_risk}</p>
                        <p className="mt-3 text-xs font-bold text-blue-700 dark:text-blue-300">
                          {education.activity || presentation.activity}
                        </p>
                        {strategy.practice_only && strategy.direction !== 'SHORT' && (
                          <p className="mt-2 text-xs font-bold text-amber-800 dark:text-amber-200">Practice only · Suggestions require your approval before any virtual action.</p>
                        )}
                        {strategy.direction === 'SHORT' && (
                          <div className="mt-3 rounded-lg bg-rose-50 p-2 text-xs font-bold leading-5 text-rose-800 dark:bg-rose-950/50 dark:text-rose-200">
                            <p>PINNGRO PRACTICE ONLY</p>
                            <p>This strategy opens SHORT positions. You profit in Practice when the stock falls after entry and lose when it rises.</p>
                          </div>
                        )}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    aria-pressed={percentageSelected}
                    onClick={() => { if (!percentageSelected) { setBotTradingStrategy(PERCENTAGE_PRESET); setPercentageSetupEpoch((value) => value + 1); } setComparisonHandoff(null); setShortStrategyAcknowledged(false); }}
                    className={`rounded-2xl border p-4 text-left transition ${percentageSelected
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20 dark:bg-blue-950/30'
                      : 'border-slate-200 hover:border-blue-300 dark:border-slate-700'}`}
                  >
                    <div className="flex items-start justify-between gap-3"><span className="font-black">Percentage Trading</span><span className="rounded-full bg-blue-100 px-2 py-1 text-[10px] font-black text-blue-800 dark:bg-blue-950 dark:text-blue-200">Customizable</span></div>
                    <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">Choose how far a stock must fall before a buy suggestion and rise before a sell suggestion.</p>
                    <p className="mt-3 text-xs font-bold text-blue-700 dark:text-blue-300">Completed daily prices · Practice approval required</p>
                  </button>
                </div>
                {percentageSelected && <div className="mt-4">
                  {comparisonHandoff?.kind === 'percentage' && comparisonHandoff.slug === botTradingStrategy && <p ref={percentageComparisonTargetRef} data-testid="percentage-config-handoff" tabIndex="-1" role="status" className="mb-3 rounded-xl bg-blue-50 p-3 text-sm font-semibold leading-6 text-blue-950 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:bg-blue-950/30 dark:text-blue-100">{comparisonHandoff.name} is selected. {botSymbols.trim() ? `Your chosen stocks are still here: ${botSymbols}.` : 'Choose your stocks next.'} {selectedCustomStrategy ? 'Review your saved percentages below, or change them to make new rules.' : 'Set your buy and sell percentages below before saving any rules.'}</p>}
                  <PercentageStrategySetup
                  key={percentageSetupEpoch}
                  client={axiosInstance} token={token}
                  initialDefinition={percentageSettingsFromDefinition(selectedCustomStrategy) ? selectedCustomStrategy : null}
                  onEditing={() => setBotTradingStrategy(PERCENTAGE_PRESET)}
                  onSaved={(definition) => {
                    if (!definition?.id) return;
                    setJustSavedPercentageStrategy({ ...definition, revision: 1 });
                    setBotTradingStrategy(`custom:${definition.id}`);
                    customStrategiesQuery.refetch();
                  }}
                /></div>}
                {(() => {
                  const factoryEntry = selectedFactoryStrategy
                    && !selectedFactoryStrategy.experimental
                    && selectedFactoryStrategy.presentation?.advanced_customization_available !== false
                    && selectedFactoryStrategy.direction === 'LONG';
                  const customEntry = selectedCustomStrategy
                    && selectedCustomStrategy.evaluator_version === 'custom-daily-rules-v3';
                  if (!factoryEntry && !customEntry) return null;
                  const sourceName = factoryEntry
                    ? strategyDisplayName(selectedFactoryStrategy.slug, selectedFactoryStrategy.name)
                    : selectedCustomStrategy.strategy_name;
                  return <div ref={protectiveComparisonTargetRef} data-testid="protective-config-handoff" tabIndex="-1" className="outline-none focus-visible:ring-2 focus-visible:ring-emerald-700">
                    {comparisonHandoff?.kind === 'protective' && comparisonHandoff.slug === botTradingStrategy && <p role="status" className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm font-semibold leading-6 text-emerald-950 dark:bg-emerald-950/30 dark:text-emerald-100">{sourceName} is selected for entry. {botSymbols.trim() ? `Your chosen stocks are still here: ${botSymbols}.` : 'Choose your stocks next.'} Configure the protective exits below.</p>}
                    <ProtectiveStrategySetup
                    key={factoryEntry ? selectedFactoryStrategy.slug : selectedCustomStrategy.id}
                    client={axiosInstance}
                    sourceName={sourceName}
                    sourceFactorySlug={factoryEntry ? selectedFactoryStrategy.slug : null}
                    sourceDefinitionId={customEntry ? selectedCustomStrategy.id : null}
                    onSaved={(definition) => {
                      if (!definition?.id) return;
                      setJustSavedPercentageStrategy({ ...definition, revision: 1 });
                      setBotTradingStrategy(`custom:${definition.id}`);
                      setComparisonProtectiveId(definition.id);
                      customStrategiesQuery.refetch();
                    }}
                    />
                  </div>;
                })()}
                {customStrategies.length > 0 && (
                  <>
                    <p className="mt-5 text-xs font-black uppercase tracking-wide text-slate-500">Your strategies</p>
                    <div className="mt-2 grid gap-3 sm:grid-cols-2" role="group" aria-label="Your strategies">
                      {customStrategies.map((strategy) => {
                        const selected = botTradingStrategy === `custom:${strategy.id}`;
                        return (
                          <button
                            key={strategy.id}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => { setBotTradingStrategy(`custom:${strategy.id}`); setComparisonHandoff(null); setPercentageSetupEpoch((value) => value + 1); setShortStrategyAcknowledged(false); }}
                            className={`rounded-2xl border p-4 text-left transition ${selected
                              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20 dark:bg-blue-950/30'
                              : 'border-slate-200 hover:border-blue-300 dark:border-slate-700'}`}
                          >
                            <span className="font-black">{strategy.strategy_name}</span>
                            <span className="ml-2 rounded-full bg-blue-100 px-2 py-1 text-[10px] font-black text-blue-800 dark:bg-blue-950 dark:text-blue-200">Your strategy</span>
                            <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">{strategy.evaluator_version === 'protective-composite-v1' ? 'Entry plus your saved protective exits' : 'Custom LONG strategy'} · Revision {strategy.revision}</p>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                <p className="mt-4 text-xs text-slate-500">Want different rules? <Link to="/settings?tab=trading" className="font-bold text-blue-600 underline dark:text-blue-300">Create custom strategy</Link>.</p>
                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                  <button type="button" onClick={() => setSimpleStep(1)} className="rounded-xl border border-slate-300 px-5 py-3 font-bold dark:border-slate-700">Back to name & stock discovery</button>
                  <button type="button" disabled={!simpleStepComplete[1]} onClick={() => setSimpleStep(3)} className="rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700 disabled:opacity-50">Next: Choose your stocks</button>
                </div>
              </div>
            )}
            {creationMode === 'simple' && selectedFactoryStrategy && (
              <div className={`mt-4 rounded-2xl border border-violet-200 bg-violet-50 p-4 text-sm leading-6 dark:border-violet-900 dark:bg-violet-950/30 ${simpleStep !== 2 ? 'hidden' : ''}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <strong>{strategyDisplayName(selectedFactoryStrategy.slug, selectedFactoryStrategy.name)}</strong>
                  {selectedFactoryStrategy.practice_only && <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-amber-800 dark:bg-slate-900 dark:text-amber-200">Practice only</span>}
                  {selectedProfileGuidance && (
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-violet-800 dark:bg-slate-900 dark:text-violet-200">
                      {botTemplate || 'Stock focus'}: {selectedProfileGuidance}
                    </span>
                  )}
                </div>
                {selectedProfileGuidance && (
                  <p className="mt-2 rounded-lg bg-white/70 p-2 text-xs dark:bg-slate-900/60">
                    <strong>Why this guidance?</strong> {pairingExplanation(botTemplate, selectedFactoryStrategy.slug, selectedProfileGuidance)} Guidance is informational and never blocks a combination.
                  </p>
                )}
                {(() => {
                  const education = FACTORY_STRATEGY_EDUCATION[selectedFactoryStrategy.slug] || {};
                  const thresholds = factoryStrategyThresholds(selectedFactoryStrategy);
                  return <>
                <p className="mt-3"><strong>Why this pairing?</strong> {pairingExplanation(botTemplate, selectedFactoryStrategy.slug, selectedProfileGuidance)}</p>
                <p className="mt-2"><strong>What should I expect?</strong> {education.activity || selectedFactoryStrategy.presentation.activity} {education.holding ? `Typical holding style: ${education.holding}.` : ''}</p>
                <p className="mt-2"><strong>What can go wrong?</strong> {education.risk || selectedFactoryStrategy.presentation.main_risk}</p>
                <div className="mt-3 grid grid-cols-2 gap-3 rounded-xl bg-white/70 p-3 text-xs dark:bg-slate-900/60">
                  <span><strong>Strategy stop-loss:</strong> {thresholds.stopLoss}</span>
                  <span><strong>Strategy take-profit:</strong> {thresholds.takeProfit}</span>
                </div>
                <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"><strong>What these rules mean:</strong> {selectedFactoryStrategy.direction === 'SHORT' ? 'A short stop-loss requests a cover after price rises beyond its threshold; a short take-profit requests a cover after price falls. Short losses are not capped, and neither threshold guarantees the later virtual fill price.' : 'A stop-loss asks the bot to exit after a position falls beyond its threshold. A take-profit asks it to exit after reaching a threshold gain. Neither guarantees the fill price.'}</p>
                <details className="mt-3 rounded-xl border border-violet-200 bg-white/70 p-3 text-xs dark:border-violet-800 dark:bg-slate-900/60">
                  <summary className="cursor-pointer font-black">Learn more · See technical rules</summary>
                  <p className="mt-2"><strong>{selectedFactoryStrategy.direction === 'SHORT' ? 'When might it open a short?' : 'When might it buy?'}</strong> {education.buy || selectedFactoryStrategy.presentation.buy}</p>
                  <p className="mt-2"><strong>{selectedFactoryStrategy.direction === 'SHORT' ? 'When might it cover the short?' : 'When might it sell?'}</strong> {education.sell || selectedFactoryStrategy.presentation.sell}</p>
                  <p className="mt-2"><strong>Market conditions:</strong> {education.market || selectedFactoryStrategy.presentation.market}</p>
                  <p className="mt-2"><strong>Who might prefer it:</strong> {education.suitableFor || selectedFactoryStrategy.presentation.suitable_for}</p>
                  <p className="mt-2"><strong>Execution:</strong> {DAILY_NEXT_OPEN_DISCLOSURE}</p>
                  <ul className="mt-3 list-disc space-y-1 pl-5">{(education.technicalRules || []).map((rule) => <li key={rule}>{rule}</li>)}</ul>
                </details>
                  </>;
                })()}
              </div>
            )}
            {creationMode === 'simple' && selectedCustomStrategy && simpleStep === 2 && (
              <div ref={selectedProtectiveSettings ? savedProtectiveTargetRef : null} tabIndex={selectedProtectiveSettings ? '-1' : undefined} data-testid={selectedProtectiveSettings ? 'saved-protective-handoff' : undefined} aria-label={selectedProtectiveSettings ? 'Selected protective exit rules' : undefined} className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-blue-900 dark:bg-blue-950/30">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <strong>{selectedCustomStrategy.strategy_name}</strong>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-blue-800 dark:bg-slate-900 dark:text-blue-200">Your strategy · Revision {selectedCustomStrategy.revision}</span>
                </div>
                <p className="mt-2">This reusable strategy defines when the bot looks to enter or exit. The saved version you choose will stay unchanged for this bot.</p>
                {selectedProtectiveSettings && <p className="mt-2"><strong>Entry:</strong> {selectedProtectiveEntryName}. <strong>Protective exits:</strong> {[
                  selectedProtectiveSettings.loss_guard && `Loss Guard ${selectedProtectiveSettings.loss_guard}%`,
                  selectedProtectiveSettings.profit_target && `Profit Target ${selectedProtectiveSettings.profit_target}%`,
                  selectedProtectiveSettings.profit_trail && `Profit Trail ${selectedProtectiveSettings.profit_trail}%`,
                ].filter(Boolean).join(' · ')}.</p>}
                {selectedProtectiveSettings && comparisonHandoff?.kind === 'protective' && <p role="status" className="mt-2 font-semibold">Your saved protective exit rules are selected. {botSymbols.trim() ? `Your chosen stocks are still here: ${botSymbols}.` : 'Choose your stocks next.'} Continue to stocks and allocation when ready.</p>}
              </div>
            )}
            {creationMode === 'advanced' && (
              <div className="mt-3 space-y-3">
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Choose a Pinngro strategy to customise, or create your own rules. Manual approval in Today still applies.
                </p>
                <details className="rounded-xl border border-slate-200 p-3 text-xs leading-5 text-slate-500 dark:border-slate-700 dark:text-slate-300">
                  <summary className="cursor-pointer font-bold text-slate-700 dark:text-slate-200">Technical details</summary>
                  <p className="mt-2">Advanced includes RSI, MACD, SMA, ATR, entry and exit rules, stop-loss, take-profit, sentiment, saved Custom Screening versions, and reproducible Backtesting. Saving changes creates a new version, so earlier bot and Backtest history stays unchanged. <Link to="/settings" className="font-bold text-blue-600 underline dark:text-blue-300">Open Strategy Workshop</Link>.</p>
                </details>
                <label className="block text-sm font-bold" htmlFor="advanced-factory-strategy">Start from a Pinngro strategy (optional)</label>
                <select
                  id="advanced-factory-strategy"
                  value={advancedFactoryStrategy}
                  onChange={(event) => {
                    const slug = event.target.value;
                    setAdvancedFactoryStrategy(slug);
                    const selected = customizableFactoryStrategies.find((strategy) => strategy.slug === slug);
                    if (slug && !templateEntries.some(([name]) => name === botTemplate)) {
                      setBotTemplate('');
                    }
                    setAdvancedStrategyDefinition(selected?.technical_definition
                      ? JSON.stringify(selected.technical_definition, null, 2)
                      : '');
                  }}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
                >
                  <option value="">Create a modern custom strategy</option>
                  {customizableFactoryStrategies.filter((strategy) => strategy.direction !== 'SHORT').map((strategy) => (
                    <option key={strategy.slug} value={strategy.slug}>{strategyDisplayName(strategy.slug, strategy.name)}</option>
                  ))}
                </select>
                {!advancedFactoryStrategy && (
                  !editingBotId || isEditableCustomEvaluator(editingEvaluatorVersion)
                ) && (
                  <CustomStrategyEditor
                    strategyName={advancedStrategyName}
                    configuration={advancedStrategyDefinition}
                    onStrategyNameChange={setAdvancedStrategyName}
                    onConfigurationChange={setAdvancedStrategyDefinition}
                    parentEvaluatorVersion={editingBotId ? editingEvaluatorVersion : null}
                    idPrefix="advanced-custom-strategy"
                  />
                )}
                {advancedFactoryStrategy && (
                  <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 dark:border-violet-900 dark:bg-violet-950/30">
                    <p className="font-bold text-violet-950 dark:text-violet-100">
                      This creates your own version of {customizableFactoryStrategies.find((strategy) => strategy.slug === advancedFactoryStrategy)?.name}. The Pinngro strategy is not changed.
                    </p>
                    <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">Use the structured controls in Strategy Workshop, or open the expert definition below when you need the complete rule set.</p>
                    <details className="mt-3 rounded-xl border border-violet-200 bg-white/70 p-3 dark:border-violet-800 dark:bg-slate-900/60">
                      <summary className="cursor-pointer text-sm font-bold">Expert strategy definition · Edit raw JSON</summary>
                      <label className="mt-3 block text-sm font-bold" htmlFor="advanced-strategy-definition">Complete raw strategy definition</label>
                      <textarea
                        id="advanced-strategy-definition"
                        value={advancedStrategyDefinition}
                        onChange={(event) => setAdvancedStrategyDefinition(event.target.value)}
                        rows={14}
                        spellCheck="false"
                        className="mt-2 w-full rounded-xl border border-slate-300 bg-white p-3 font-mono text-xs dark:border-slate-700 dark:bg-slate-950"
                      />
                    </details>
                  </div>
                )}
              </div>
            )}
            <div className={!editingBotId && creationMode === 'simple' && simpleStep !== 3 ? 'hidden' : ''}>
            {!editingBotId && creationMode === 'simple' && <SetupSoFar includeStrategy />}
            {creationMode === 'simple' ? (
              <h3 className="mt-5 text-sm font-bold">4. Choose your stocks</h3>
            ) : (
              <h3 className="mt-5 text-sm font-bold">2. Symbols to watch</h3>
            )}
            {creationMode === 'simple' && stockDiscoveryMode === 'STOCK_FOCUS' && botTemplate && (
              <section className="mt-3 rounded-2xl border border-cyan-200 bg-cyan-50/70 p-4 dark:border-cyan-900 dark:bg-cyan-950/20" aria-labelledby="stock-focus-candidates-heading">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h4 id="stock-focus-candidates-heading" className="font-black">Stocks matching your selected focus</h4>
                    <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                      Every stock in this group currently meets the criteria for {botTemplate}. Choose them yourself, browse the full list, or let Pinngro randomly select a starting set for you.
                    </p>
                  </div>
                  <span className="w-fit rounded-full bg-white px-3 py-1 text-[10px] font-black uppercase tracking-wide text-cyan-800 dark:bg-slate-900 dark:text-cyan-200">Candidates, not recommendations</span>
                </div>
                <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100" aria-labelledby="pick-for-me-heading">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.16em] text-blue-700 dark:text-blue-300">Stock Focus helper</p>
                      <h5 id="pick-for-me-heading" className="mt-1 flex items-center gap-2 text-lg font-black"><FaDice aria-hidden="true" />Pick for me</h5>
                      <p className="mt-1 max-w-2xl text-xs leading-5">Don&apos;t know where to start? Pinngro can randomly choose a starting set from every current match. There is no hidden ranking or prediction.</p>
                    </div>
                  </div>
                  {pickResult?.eligibleCount === 0 ? (
                    <p role="status" className="mt-3 rounded-xl bg-white/70 p-3 text-xs font-semibold dark:bg-slate-950/50">
                      {pickResult.stockFocusMatchCount > 0
                        ? `${pickResult.stockFocusMatchCount} stocks currently match this Stock Focus, but none can currently be used in Practice. Browse the matches to review their Stock Focus evidence.`
                        : 'No stocks currently match this Stock Focus.'}
                    </p>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2" aria-label="Random Stock Focus selection size">
                      {[5, 10, 20].map((count) => (
                        <button
                          type="button"
                          key={count}
                          disabled={pickLoading}
                          onClick={() => pickForMe(count)}
                          className={`min-h-11 rounded-xl px-4 py-2 text-sm font-black transition ${count === 5 ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300' : 'border border-blue-300 bg-white text-blue-900 hover:bg-blue-100 disabled:text-blue-400 dark:border-blue-800 dark:bg-slate-950 dark:text-blue-100 dark:hover:bg-blue-950'}`}
                        >
                          {pickLoading ? 'Picking…' : `Pick ${count}`}
                        </button>
                      ))}
                    </div>
                  )}
                  {pickResult && pickResult.eligibleCount > 0 && (
                    <div className="mt-3 rounded-xl bg-white/70 p-3 text-xs leading-5 shadow-sm dark:bg-slate-950/50 motion-safe:animate-pulse">
                      <p className="font-black">
                        {pickResult.selectedCount < pickResult.requestedCount
                          ? `${pickResult.eligibleCount} of ${pickResult.stockFocusMatchCount} Stock Focus matches can currently be used in Practice, so I&apos;ve selected all ${pickResult.selectedCount}.`
                          : `${pickResult.selectedCount} random stocks selected from ${pickResult.eligibleCount} of ${pickResult.stockFocusMatchCount} Stock Focus matches that can currently be used in Practice.`}
                      </p>
                      <p className="mt-1">{pickKeptCount} kept · {Math.max(pickFinalCount - pickKeptCount, 0)} will change. Each new pick is random from the same qualifying Stock Focus — no ranking or prediction involved.</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {pickRollLabel ? <button type="button" onClick={() => pickForMe(pickResult.requestedCount)} disabled={pickLoading} className="min-h-11 rounded-lg bg-blue-600 px-3 py-2 font-black text-white hover:bg-blue-700 disabled:opacity-60">{pickLoading ? 'Picking…' : pickRollLabel}</button> : <p className="inline-flex min-h-11 items-center font-black text-emerald-700 dark:text-emerald-300">All {pickFinalCount} kept — your selection is ready.</p>}
                        {pickUndoSnapshot && <button type="button" onClick={undoPickForMe} className="min-h-11 rounded-lg border border-blue-300 px-3 py-2 font-black hover:bg-blue-100 dark:border-blue-800 dark:hover:bg-blue-950">Undo</button>}
                        <a href="#bot-symbols" className="inline-flex min-h-11 items-center rounded-lg border border-blue-300 px-3 py-2 font-black hover:bg-blue-100 dark:border-blue-800 dark:hover:bg-blue-950">Review selection</a>
                      </div>
                      <details className="mt-3"><summary className="cursor-pointer font-bold">Why are these stocks here?</summary><p className="mt-1">These stocks were chosen randomly from the companies that currently meet the criteria for {botTemplate}.</p></details>
                      <ul className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                        {(pickResult.selectedCompanies || []).filter((item) => simpleSymbols.includes(item.symbol)).map((item) => { const isKept = (pickResult.keptSymbols || []).includes(item.symbol); return <li key={item.symbol} className="flex items-center justify-between gap-3 p-3"><div className="min-w-0"><span className="font-black">{item.symbol}</span>{item.company_name && <span className="ml-2 text-slate-500 dark:text-slate-400">{item.company_name}</span>}</div><div className="flex shrink-0 flex-wrap justify-end gap-3"><button type="button" aria-pressed={isKept} onClick={() => togglePickedKeep(item.symbol)} className="min-h-11 font-bold text-blue-700 hover:underline dark:text-blue-300">{isKept ? '✓ Kept' : 'Keep'}</button><button type="button" onClick={(event) => openCompanyContext(item, event.currentTarget)} className="min-h-11 font-bold text-blue-700 hover:underline dark:text-blue-300">About</button><button type="button" onClick={() => removePickedStock(item.symbol)} className="min-h-11 font-bold text-slate-700 hover:underline dark:text-slate-200">Remove</button></div></li>; })}
                      </ul>
                    </div>
                  )}
                </div>
                {candidateNotice && <p role="status" aria-live="polite" className="mt-3 text-xs font-bold text-blue-800 dark:text-blue-200">{candidateNotice}</p>}
              </section>
            )}
            <label className="mt-4 block text-xs font-bold text-slate-600 dark:text-slate-300" htmlFor="bot-symbols">
              Selected stock symbols
            </label>
            <input
              id="bot-symbols"
              aria-describedby="bot-symbols-guidance"
              value={botSymbols}
              onChange={(event) => {
                const value = event.target.value;
                const nextSymbols = parseSymbolInput(value);
                setBotSymbols(value);
                if (pickResult) {
                  const added = nextSymbols.filter((symbol) => !simpleSymbols.includes(symbol));
                  setPickResult((current) => (current ? {
                    ...current,
                    selectedCount: nextSymbols.length,
                    keptSymbols: Array.from(new Set([
                      ...(current.keptSymbols || []).filter((symbol) => nextSymbols.includes(symbol)),
                      ...added,
                    ])),
                    selectedCompanies: nextSymbols.map((symbol) => (
                      (current.selectedCompanies || []).find((item) => item.symbol === symbol) || { symbol, company_name: null, kept: added.includes(symbol) }
                    )),
                  } : current));
                  setCandidateNotice(added.length ? 'Added manually · Kept for future picks.' : 'Your random selection was updated.');
                } else {
                  setScreeningLineage(null);
                  setSelectionMethod(null);
                }
              }}
              placeholder="AAPL, MSFT, NVDA"
              className="mt-2 w-full rounded-xl border border-slate-300 bg-transparent px-4 py-3 uppercase outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700"
            />
            <p id="bot-symbols-guidance" className="mt-2 text-xs leading-5 text-slate-500">
              {stockDiscoveryMode === 'ALL_STOCKS'
                ? 'Search the full supported stock list and select only the companies you want this bot to watch.'
                : 'Pinngro has filtered the candidates using your Stock Focus. Now select the companies you want this bot to watch.'} Stocks selected while browsing appear here. If you know the tickers, enter a comma-separated selected list; Pinngro validates support when you create the bot.
            </p>
            {stockDiscoveryMode === 'STOCK_FOCUS' && simpleSymbols.length > 0 && (
              <details className="mt-3 rounded-xl border border-slate-200 p-3 text-xs dark:border-slate-800">
                <summary className="cursor-pointer font-bold">Review selected companies</summary>
                <div className="mt-3 flex flex-wrap gap-2">
                  {simpleSymbols.map((symbol) => {
                    const picked = (pickResult?.selectedCompanies || []).find((item) => item.symbol === symbol);
                    const stock = picked || { symbol };
                    return <button key={symbol} type="button" onClick={(event) => openCompanyContext(stock, event.currentTarget)} className="min-h-11 rounded-lg border border-slate-300 px-3 py-2 text-left font-bold hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"><span>{symbol}</span>{(stock.company_name || stock.companyName) && <span className="ml-1 font-normal text-slate-500 dark:text-slate-400">· {stock.company_name || stock.companyName}</span>}<span className="ml-2 text-blue-700 underline dark:text-blue-300">About</span></button>;
                  })}
                </div>
              </details>
            )}
            <p role="status" aria-live="polite" className="mt-2 text-sm font-black text-blue-700 dark:text-blue-300">
              Selected: {simpleSymbols.length} {simpleSymbols.length === 1 ? 'stock' : 'stocks'}
            </p>
            {simpleSymbols.length === 0 && (
              <p className="mt-1 text-xs font-semibold text-slate-600 dark:text-slate-300">Select at least 1 stock to continue.</p>
            )}
            {simpleSymbols.length >= 50 && (
              <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                You selected {simpleSymbols.length} stocks. This may spread the bot&apos;s Maximum Allocation across many symbols. You can continue, but review the list carefully.
              </p>
            )}
            {!editingBotId && creationMode === 'simple' && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Link
                  to="/stock-explorer?setup=practice"
                  className="rounded-xl bg-cyan-600 px-4 py-2.5 text-center text-sm font-bold text-white hover:bg-cyan-700"
                >
                  {stockDiscoveryMode === 'ALL_STOCKS' ? 'Browse All Stocks' : 'Browse all matching stocks'}
                </Link>
                <button
                  type="button"
                  onClick={() => document.getElementById('bot-symbols')?.focus()}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                >
                  Enter symbols manually
                </button>
              </div>
            )}
            {!editingBotId && creationMode === 'simple' && (
              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <button type="button" onClick={() => setSimpleStep(2)} className="rounded-xl border border-slate-300 px-5 py-3 font-bold dark:border-slate-700">Back to Trading Strategy</button>
                <button type="button" disabled={!simpleStepComplete[2]} onClick={() => setSimpleStep(4)} className="rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700 disabled:opacity-50">Next: Set maximum allocation</button>
              </div>
            )}
            </div>
            <div className={!editingBotId && creationMode === 'simple' && simpleStep !== 4 ? 'hidden' : ''}>
            {!editingBotId && creationMode === 'simple' && <SetupSoFar includeStrategy includeStocks />}
            <label className="mt-5 block text-sm font-bold" htmlFor="risk-budget">
              {creationMode === 'simple' ? '5. Set maximum allocation' : '3. Maximum allocation'}
            </label>
            <div className="relative mt-2">
              <input
                id="risk-budget"
                type="number"
                value={riskBudget}
                onChange={(event) => setRiskBudget(event.target.value)}
                min="0.01"
                max="100"
                step="0.01"
                required
                className="w-full rounded-xl border border-slate-300 bg-transparent px-4 py-3 pr-10 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700"
              />
              <span className="absolute right-4 top-3 text-slate-500">%</span>
            </div>
            <p className="mt-2 text-xs leading-5 text-slate-500"><strong>Bot maximum allocation</strong> is the most of your virtual Pinngro Practice portfolio this bot may use. It is separate from the selected strategy&apos;s stop-loss, take-profit, and entry rules. Other bots and your overall Practice safety limits still apply.</p>
            {!editingBotId && creationMode === 'simple' && (
              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <button type="button" onClick={() => setSimpleStep(3)} className="rounded-xl border border-slate-300 px-5 py-3 font-bold dark:border-slate-700">Back to Stocks</button>
                <button type="button" disabled={!simpleStepComplete[3]} onClick={() => setSimpleStep(5)} className="rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700 disabled:opacity-50">Next: Review your bot</button>
              </div>
            )}
            </div>
            {formError && (
              <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{formError}</p>
            )}
            {formSuccess && (
              <p className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">{formSuccess}</p>
            )}
            {!editingBotId && creationMode === 'simple' && (
              <div className={`mt-5 rounded-xl border border-cyan-200 bg-cyan-50 p-4 text-sm leading-6 text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100 ${simpleStep < 5 ? 'hidden' : ''}`}>
                <strong>6. Review your bot</strong>
                <p className="mt-2">{PRACTICE_EXPLANATION} Creating the bot only saves it; nothing runs until you explicitly start Pinngro Practice.</p>
                {selectedFactoryStrategy && (() => {
                  const education = FACTORY_STRATEGY_EDUCATION[selectedFactoryStrategy.slug] || {};
                  const thresholds = factoryStrategyThresholds(selectedFactoryStrategy);
                  return (
                    <div className="mt-4 space-y-2 border-t border-cyan-200 pt-4 dark:border-cyan-800">
                      <p><strong>Your bot will</strong></p>
                      <p><strong>Find stocks:</strong> {stockDiscoveryMode === 'ALL_STOCKS' ? 'All Stocks — chosen directly by you' : botTemplate || 'Choose a Stock Focus'}</p>
                      <p><strong>Trading Strategy:</strong> {strategyDisplayName(selectedFactoryStrategy.slug, selectedFactoryStrategy.name)}</p>
                      {selectedFactoryStrategy.practice_only && <p><strong>Availability:</strong> Practice only. This strategy does not place Alpaca Paper or Live orders.</p>}
                      <p><strong>Direction:</strong> {selectedFactoryStrategy.direction || 'LONG'}</p>
                      <p><strong>Stocks:</strong> {parseSymbolInput(botSymbols).join(', ') || 'Choose at least one stock'}</p>
                      {stockDiscoveryMode === 'ALL_STOCKS' ? (
                        <p className="rounded-xl border border-cyan-200 bg-white/70 p-3 dark:border-cyan-800 dark:bg-slate-900/60"><strong>Stock check:</strong> Pinngro will confirm these choices are in the supported stock list when you create the bot. No Stock Focus is attached.</p>
                      ) : screeningLineage && screeningLineage.profileId === botTemplate ? (
                        <div className="rounded-xl border border-cyan-200 bg-white/70 p-3 dark:border-cyan-800 dark:bg-slate-900/60">
                          <p><strong>Stock Focus match:</strong> Confirmed from the current published candidate list.</p>
                          {selectionMethod === 'RANDOM_STOCK_FOCUS' && (
                            <p className="mt-2"><strong>Selection:</strong> Randomly selected from the current qualifying Stock Focus candidates, then reviewed by you.</p>
                          )}
                          <details className="mt-2 text-xs"><summary className="cursor-pointer font-bold">Technical selection details</summary><p className="mt-2 break-all font-mono">Generation {screeningLineage.generationId}</p><p className="mt-1 break-all font-mono">Hash {screeningLineage.generationHash}</p><p className="mt-1">Profile schema {screeningLineage.profileSchemaVersion}</p><p className="mt-1 break-all font-mono">Bundle {screeningLineage.profileBundleHash}</p></details>
                        </div>
                      ) : (
                        <p className="rounded-xl border border-cyan-200 bg-white/70 p-3 dark:border-cyan-800 dark:bg-slate-900/60"><strong>Stock check:</strong> Pinngro will confirm your manually entered choices match this Stock Focus when you create the bot.</p>
                      )}
                      <p><strong>Bot maximum allocation:</strong> {riskBudget ? formatPercent(riskBudget) : '—'} — this limits virtual exposure and does not change the strategy rules below.</p>
                      <p><strong>Strategy thresholds:</strong> Stop-loss {thresholds.stopLoss} · Take-profit {thresholds.takeProfit}. These come from the selected strategy and are separate from the bot maximum allocation.</p>
                      {selectedFactoryStrategy.direction === 'SHORT' ? <>
                        <p><strong>Practice short limit:</strong> the lower of this allocation, 10% of pre-entry Practice equity, and available unreserved Practice cash.</p>
                        <p><strong>OPEN SHORT:</strong> {education.buy || selectedFactoryStrategy.presentation.buy}</p>
                        <p><strong>COVER SHORT:</strong> {education.sell || selectedFactoryStrategy.presentation.sell}</p>
                        <p><strong>Strategy risk rules:</strong> {thresholds.risk} Short losses can grow rapidly if a stock rises.</p>
                        <p><strong>Practice only:</strong> No real short sale, Alpaca Paper or Live order, borrowed shares, margin, or locate request.</p>
                        <p><strong>Execution:</strong> completed daily signals → next eligible session open after manual Practice approval.</p>
                        <p className="rounded-xl border border-rose-300 bg-rose-50 p-3 font-bold text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100">Daily stop rules request a later virtual cover. They cannot guarantee the final exit price. Pinngro Practice does not model all real-world short-selling costs or borrow availability.</p>
                        <label className="flex items-start gap-3 rounded-xl border border-slate-300 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                          <input type="checkbox" checked={shortStrategyAcknowledged} onChange={(event) => setShortStrategyAcknowledged(event.target.checked)} className="mt-1" />
                          <span>I understand this strategy opens virtual SHORT positions and can lose money in Practice when stock prices rise.</span>
                        </label>
                      </> : <>
                        <p><strong>It may buy when:</strong> {education.buy || selectedFactoryStrategy.presentation.buy}</p>
                        <p><strong>It may sell when:</strong> {education.sell || selectedFactoryStrategy.presentation.sell}</p>
                      </>}
                      <p><strong>Practice execution:</strong> {DAILY_NEXT_OPEN_DISCLOSURE}</p>
                      <p><strong>Approval policy:</strong> Manual approval required. There is no automatic or bulk approval.</p>
                      <p><strong>Real money:</strong> No. This bot is created for Pinngro Practice and cannot place a real or Alpaca Paper order.</p>
                    </div>
                  );
                })()}
                {selectedCustomStrategy && (
                  <div className="mt-4 space-y-2 border-t border-cyan-200 pt-4 dark:border-cyan-800">
                    <p><strong>Your bot will</strong></p>
                    <p><strong>Find stocks:</strong> {stockDiscoveryMode === 'ALL_STOCKS' ? 'All Stocks — chosen directly by you' : botTemplate}</p>
                    <p><strong>Trading Strategy:</strong> {selectedCustomStrategy.strategy_name}</p>
                    {selectedProtectiveSettings && <p><strong>Entry:</strong> {selectedProtectiveEntryName}. <strong>Protective exits:</strong> {[
                      selectedProtectiveSettings.loss_guard && `Loss Guard ${selectedProtectiveSettings.loss_guard}% from the virtual entry fill`,
                      selectedProtectiveSettings.profit_target && `Profit Target ${selectedProtectiveSettings.profit_target}% from the virtual entry fill`,
                      selectedProtectiveSettings.profit_trail && `Profit Trail ${selectedProtectiveSettings.profit_trail}% from the highest verified completed close since entry`,
                    ].filter(Boolean).join(' · ')}.</p>}
                    <p><strong>Strategy source:</strong> Your strategy · Revision {selectedCustomStrategy.revision}</p>
                    <p><strong>Direction:</strong> LONG</p>
                    <p><strong>Stocks:</strong> {parseSymbolInput(botSymbols).join(', ') || 'Choose at least one stock'}</p>
                    <p><strong>Bot maximum allocation:</strong> {riskBudget ? formatPercent(riskBudget) : '—'} — separate from this strategy&apos;s entry and exit rules.</p>
                    <p><strong>Practice execution:</strong> {DAILY_NEXT_OPEN_DISCLOSURE}</p>
                    <p><strong>Approval policy:</strong> Manual approval required. There is no automatic or bulk approval.</p>
                    <p><strong>Real money:</strong> No. This bot is created for Pinngro Practice and cannot place a real or Alpaca Paper order.</p>
                  </div>
                )}
              </div>
            )}
            {!editingBotId && creationMode === 'simple' && simpleStep === 5 && (
              <button type="button" onClick={() => setSimpleStep(4)} className="mt-5 w-full rounded-xl border border-slate-300 px-5 py-3 font-bold dark:border-slate-700">Back to allocation</button>
            )}
            <button
              ref={revisionTriggerRef}
              type="submit"
              disabled={savingBot || (
                !editingBotId
                && creationMode === 'simple'
                && !simpleStepComplete[4]
              )}
              aria-describedby={
                !editingBotId && creationMode === 'simple' && !simpleStepComplete[4]
                  ? 'create-bot-guidance'
                  : undefined
              }
              aria-live="polite"
              className={`mt-4 w-full rounded-xl bg-blue-600 px-5 py-3 font-black text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 ${!editingBotId && creationMode === 'simple' && simpleStep !== 5 ? 'hidden' : ''}`}
            >
                  {savingBot ? editingBotId ? 'Saving…' : 'Creating…' : editingBotId ? 'Save changes' : creationMode === 'simple'
                    ? 'Create bot'
                    : advancedFactoryStrategy ? 'Create customized Practice bot' : 'Save advanced draft'}
            </button>
            {!editingBotId && creationMode === 'simple' && simpleStep === 5 && !simpleStepComplete[4] && (
              <p id="create-bot-guidance" className="mt-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
                {botTradingStrategy === 'the-big-short' && !shortStrategyAcknowledged
                  ? 'Confirm the Practice short-strategy acknowledgement to create this bot.'
                  : !botName.trim() || botName.trim().length < 3
                    ? 'Enter a bot name of at least 3 characters to continue.'
                    : stockDiscoveryMode === 'STOCK_FOCUS' && !botTemplate
                      ? 'Choose a Stock Focus to continue.'
                      : !botTradingStrategy
                        ? 'Choose a strategy to continue.'
                        : simpleSymbols.length === 0
                          ? 'Select at least one stock to continue.'
                          : 'Enter a maximum allocation between 0.01% and 100% to continue.'}
              </p>
            )}
            {editingBotId && (
              <button
                type="button"
                onClick={resetBotForm}
                className="mt-2 w-full rounded-xl px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel editing
              </button>
            )}
          </form>
          {!showCreator && !editingBotId && formSuccess && !createdBotId && (
            <section className={`${firstBotJourney ? 'order-1' : 'order-2'} rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100`}>
              <h2 className="text-2xl font-black">Bot saved</h2>
              <p className="mt-2">{formSuccess}</p>
              <button type="button" onClick={openBotCreator} className="mt-4 rounded-xl border border-emerald-500 px-5 py-3 text-sm font-bold">Create another bot</button>
            </section>
          )}
        </section>

        <details className="mt-12 rounded-3xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <summary className="cursor-pointer list-none">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Stock Focus reference</p>
            <h2 className="mt-2 text-2xl font-black">Browse all eight Stock Focus profiles</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Open this reference when you want to compare what kinds of stocks each profile considers.</p>
          </summary>
        <section className="mt-8">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">
                Stock Focus
              </p>
              <h2 className="mt-2 text-3xl font-black tracking-tight">Choose a Stock Focus</h2>
              <p className="mt-2 text-slate-600 dark:text-slate-400">
                What kinds of companies should Pinngro look for? A Stock Focus screens for candidates; it does not decide when to buy or sell.
              </p>
            </div>
            <Link
              to="/settings"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700"
            >
              Build a custom strategy <FaArrowRight />
            </Link>
          </div>

          {templatesQuery.isLoading ? (
            <div className="py-16"><LoadingSpinner /></div>
          ) : templatesQuery.isError ? (
            <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              We couldn&apos;t load your strategy templates. Your existing configurations were not changed.
            </div>
          ) : (
            <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {templateEntries.map(([name, template], index) => {
                const [gradient, badge] = BOT_ACCENTS[index % BOT_ACCENTS.length];
                const criteria = summarizeCriteria(template);
                const configurePath = `/settings?template=${encodeURIComponent(name)}`;
                const guidance = PRESET_GUIDANCE[name];
                return (
                  <article
                    key={name}
                    className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className={`h-2 bg-gradient-to-r ${gradient}`} />
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${badge}`}>
                          <FaRobot className="text-xl" />
                        </div>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          Ready to configure
                        </span>
                      </div>
                      <h3 className="mt-5 text-xl font-black">{name}</h3>
                      <div className="mt-3 space-y-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                        <p>{guidance?.looksFor || template.description}</p>
                        <p><strong className="text-slate-800 dark:text-slate-200">Risk style:</strong> {guidance?.risk || 'Review the company and allocation risks before starting.'}</p>
                        <p><strong className="text-slate-800 dark:text-slate-200">Suitable for:</strong> {guidance?.suitableFor || 'People who understand the selected companies.'}</p>
                      </div>
                      <details className="mt-5 border-y border-slate-100 py-4 dark:border-slate-800">
                        <summary className="cursor-pointer text-sm font-bold text-blue-700 dark:text-blue-300">Learn more</summary>
                        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300"><strong>What makes it different:</strong> {guidance?.difference || 'Review its screening criteria before selecting stocks.'}</p>
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300"><strong>What it does not guarantee:</strong> {guidance?.notGuarantee || 'A screen identifies candidates; it does not guarantee quality or returns.'}</p>
                        <p className="mt-2 text-sm font-bold">Technical screening details</p>
                        <div className="mt-3 space-y-2">
                          {criteria.map((criterion) => (
                            <div key={criterion.label} className="flex items-center justify-between gap-4 text-sm">
                              <span className="text-slate-500 dark:text-slate-400">{criterion.label}</span>
                              <span className="font-bold">{criterion.value}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                      <button
                        type="button"
                        onClick={() => {
                          setCreationMode('simple');
                          setBotTemplate(name);
                          setScreeningLineage(null);
                          setSelectionMethod(null);
                          document.getElementById('hire')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        Use this profile <FaArrowRight className="transition group-hover:translate-x-1" />
                      </button>
                      <Link to={`${configurePath}&advanced=1`} className="mt-3 block text-xs font-bold text-slate-500 hover:text-blue-700 dark:hover:text-blue-300">Customize technical screening in Advanced</Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
        </details>

        {queryEnabled && (
          <details className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <summary className="cursor-pointer list-none">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-400">Trading Strategy comparison</p>
            <h2 className="mt-2 text-2xl font-black">Compare trading strategies</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300"><strong>Stock focus, not a trading algorithm:</strong> the profiles choose candidates. After choosing this focus, select separately how the bot enters, holds, and exits.</p>
            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">{factoryStrategiesQuery.isSuccess ? `${factoryStrategies.length} predefined ${factoryStrategies.length === 1 ? 'strategy' : 'strategies'} plus configurable Percentage Trading.` : 'Compare available strategies with configurable Percentage Trading when their descriptions are available.'} Choose a strategy here to open Create Bot with it selected.</p>
            </summary>
            {factoryStrategiesQuery.isError && <p role="alert" className="mt-4 text-sm text-amber-800 dark:text-amber-200">{factoryStrategiesQuery.error?.response?.status === 403 ? 'This account cannot use the predefined strategies right now. Check your account status in Settings or contact support.' : 'Predefined strategy choices are temporarily unavailable. Try again later.'}</p>}
            {customStrategiesQuery.isError && <p role="alert" className="mt-4 text-sm text-amber-800 dark:text-amber-200">Your saved strategies are temporarily unavailable. You can still configure new Percentage Trading or protective exit rules.</p>}
            <p className="mt-4 text-xs text-slate-500 sm:hidden">Swipe sideways to compare style, activity, holding, risk and exit rules.</p>
            <div className="mt-5 overflow-x-auto">
              <table className="min-w-[760px] w-full text-left text-sm">
                <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800"><th className="p-3">Strategy</th><th className="p-3">Style</th><th className="p-3">Activity</th><th className="p-3">Holding style</th><th className="p-3">Relative risk</th><th className="p-3">Exit approach</th></tr></thead>
                <tbody>{factoryStrategies.map((strategy) => {
                  const education = FACTORY_STRATEGY_EDUCATION[strategy.slug] || {};
                  const displayName = strategyDisplayName(strategy.slug, strategy.name);
                  return <tr key={strategy.slug} className="border-b border-slate-100 dark:border-slate-800"><td className="p-3 font-black"><div>{displayName}</div>{strategy.practice_only && <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-900 dark:bg-amber-950 dark:text-amber-200">Practice only</span>}<button type="button" onClick={() => selectComparedStrategy(strategy.slug, displayName)} aria-label={`Use ${displayName} strategy in Create Bot`} className="mt-3 inline-flex min-h-11 items-center rounded-lg bg-blue-600 px-3 py-2 text-left text-xs font-bold text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">Use this strategy <FaArrowRight className="ml-2 shrink-0" aria-hidden="true" /></button></td><td className="p-3">{education.style}</td><td className="p-3">{education.activity}</td><td className="p-3">{education.holding}</td><td className="p-3">{education.relativeRisk}</td><td className="p-3">{education.sell}</td></tr>;
                })}
                <tr data-testid="percentage-comparison-row" className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-3 font-black"><div>Percentage Trading</div><span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-blue-800 dark:bg-blue-950 dark:text-blue-200">Configurable</span><button id="comparison-use-percentage" type="button" onClick={() => selectComparedStrategy(PERCENTAGE_PRESET, 'Percentage Trading', 'percentage')} aria-label="Use Percentage Trading strategy in Create Bot" className="mt-3 inline-flex min-h-11 items-center rounded-lg bg-blue-600 px-3 py-2 text-left text-xs font-bold text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">Use this strategy <FaArrowRight className="ml-2 shrink-0" aria-hidden="true" /></button><p className="mt-2 text-xs font-normal text-slate-600 dark:text-slate-300">Set and save your exact percentages in Create Bot. The starting values are editable examples.</p></td>
                  <td className="p-3">{percentageExample.buy}</td>
                  <td className="p-3 align-top">Checks completed daily prices; suggestions require Practice approval.</td>
                  <td className="p-3 align-top">Variable; no fixed maximum holding period.</td>
                  <td className="p-3 align-top">Depends on the stock and chosen thresholds; no automatic stop loss.</td>
                  <td className="p-3 align-top">{percentageExample.sell}</td>
                </tr>
                {savedPercentageStrategies.map((strategy) => {
                  const details = percentageComparisonDetails(strategy);
                  return <tr key={strategy.id} data-testid="saved-percentage-comparison-row" className="border-b border-slate-100 dark:border-slate-800">
                    <td className="p-3 font-black"><div>{strategy.strategy_name}</div><span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-blue-800 dark:bg-blue-950 dark:text-blue-200">Saved Percentage Trading rules</span><button type="button" onClick={() => selectComparedStrategy(`custom:${strategy.id}`, strategy.strategy_name, 'percentage')} aria-label={`Use ${strategy.strategy_name} strategy in Create Bot`} className="mt-3 inline-flex min-h-11 items-center rounded-lg bg-blue-600 px-3 py-2 text-left text-xs font-bold text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">Use this strategy <FaArrowRight className="ml-2 shrink-0" aria-hidden="true" /></button></td>
                    <td className="p-3">{details.buy}</td><td className="p-3">Checks completed daily prices; suggestions require Practice approval.</td><td className="p-3">Variable; no fixed maximum holding period.</td><td className="p-3">Depends on the stock and saved thresholds; no automatic stop loss.</td><td className="p-3">{details.sell}</td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
            <section className="mt-8 border-t border-slate-200 pt-6 dark:border-slate-800" aria-labelledby="protective-comparison-title">
              <h3 id="protective-comparison-title" className="text-lg font-black">Protective Exit Rules</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">Loss Guard, Profit Target and Profit Trail can request an exit after a compatible entry strategy opens a position. They cannot open a position on their own.</p>
              <label htmlFor="protective-comparison-configuration" className="mt-4 block text-sm font-bold">Rules to compare</label>
              <select id="protective-comparison-configuration" value={comparisonProtectiveId} onChange={(event) => setComparisonProtectiveId(event.target.value)} className="mt-2 min-h-11 w-full max-w-xl rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 dark:border-slate-700 dark:bg-slate-950 dark:text-white">
                <option value="example">Examples: 5% Loss Guard / 8% Profit Target / 5% Profit Trail</option>
                {comparisonProtectiveId !== 'example' && !comparisonProtectiveStrategy && <option value={comparisonProtectiveId}>Previously selected saved rules unavailable</option>}
                {savedProtectiveStrategies.map((strategy) => <option key={strategy.id} value={strategy.id}>Saved: {strategy.strategy_name}</option>)}
              </select>
              <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">Examples are not saved rules. A saved combined strategy keeps your exact entry and exit settings.</p>
              {comparisonProtectiveExits && <button type="button" onClick={() => selectComparedStrategy(comparisonProtectiveStrategy ? `custom:${comparisonProtectiveStrategy.id}` : 'trend-pullback', comparisonProtectiveStrategy?.strategy_name || 'Trend Retracement', 'protective')} aria-label={comparisonProtectiveStrategy ? `Use ${comparisonProtectiveStrategy.strategy_name} protective exits in Create Bot` : 'Configure protective exits with Trend Retracement in Create Bot'} className="mt-3 inline-flex min-h-11 items-center rounded-lg bg-emerald-700 px-4 py-2 text-sm font-bold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700">{comparisonProtectiveStrategy ? 'Use saved exit rules' : 'Configure exits with Trend Retracement'} <FaArrowRight className="ml-2 shrink-0" aria-hidden="true" /></button>}
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {[
                  ['loss_guard', 'Loss Guard', 'Decline from the recorded virtual entry fill', 'A gap may produce a worse next-open fill.'],
                  ['profit_target', 'Profit Target', 'Gain from the recorded virtual entry fill', 'An earlier exit can miss later gains.'],
                  ['profit_trail', 'Profit Trail', 'Decline from the highest verified completed close since entry', 'A reversal or gap can produce a lower next-open fill.'],
                ].map(([key, label, reference, risk]) => <div key={key} data-testid={`${key}-comparison-row`} className="rounded-xl border border-slate-200 p-4 text-sm leading-6 dark:border-slate-700">
                  <h4 className="font-black">{label}</h4>
                  <p className="mt-1">{comparisonProtectiveExits ? comparisonProtectiveExits[key] == null ? 'Not selected in these saved rules.' : `${comparisonProtectiveExits[key]}% ${reference}; possible virtual fill at the next eligible open.` : 'Saved settings unavailable.'}</p>
                  <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">{risk} Practice approval remains required.</p>
                </div>)}
              </div>
            </section>
            <p className="mt-4 text-xs text-slate-500">These descriptions compare rules, not historical returns or rankings. Historical results require an entitled Backtest of a saved bot against a compatible finalized split-adjusted daily snapshot. Backtests use completed-session signals, next-session virtual fills and their stated allocation, commission and slippage assumptions; past results do not predict future returns.</p>
          </details>
        )}

        {customScreeningDefinitions.length > 0 && (
          <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <FaCheckCircle className="text-emerald-500" />
              <div>
                <h2 className="font-bold">Your Custom Screening definitions</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  {customScreeningDefinitions.length} saved screening rule set{customScreeningDefinitions.length === 1 ? '' : 's'} available through Strategy Workshop. Each saved version stays unchanged.
                </p>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

const PaperMetric = ({ label, value }) => <div className="rounded-lg bg-white/70 p-2 dark:bg-slate-900/60"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</p><p className="mt-1 font-black text-slate-900 dark:text-white">{value}</p></div>;

export default BotsPage;
